/* PHONE_FIRST × PRODUCT checkout screen hook — logic extracted from journey (Phase 4 §4.1). */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  prepareSellerAssistedJwtRebindAtPaymentStart,
  isSellerAssistedCheckoutOrigin,
  isSellerAssistedPaymentBlockedByStaleHandoff,
  resolveSellerAssistedDisplayLines,
} from '../../seller-assisted/index.js';
import type {
  CheckoutSessionDisplayLine,
  CurrencyCode,
  PickupHandoffModeV3,
  SessionMetadataCollect,
} from 'pi-kiosk-shared';
import { isMethodPayableForCount, resolveLocalizedName } from 'pi-kiosk-shared';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import {
  rememberSalesPointIdForTenant,
  readLastKnownSalesPointSlug,
} from '../../../shared/session/lastKnownSalesPoint.js';
import {
  readCachedCheckoutSession as readCachedCheckoutSessionFromQueryClient,
  useGetCheckoutSession,
  useStartCashPayment,
  useStartGatewayPayment,
  useStartQrPayment,
  useUpdateCheckoutSessionCollect,
  usePaymentStatus,
  useCreateCheckoutSession,
  type CashPaymentStartResponseDTO,
  type CheckoutSessionDTO,
  type CheckoutSessionLineDTO,
  type GatewayPaymentResponseDTO,
  type QRPaymentResponseDTO,
} from '../../checkout/checkoutApi.js';
import type { ShippingCheckoutPayload } from '../../checkout/shippingAddressTypes.js';
import {
  canShowCheckoutCurrencyPicker,
  isCheckoutCurrencyLocked,
  resolveAcceptedCheckoutCurrencies,
  resolveDisplayCurrencyFromSession,
  writeStickyCheckoutCurrency,
} from '../../checkout/checkoutCurrency.js';
import { isSelfScanCheckoutSearch } from '../../self-scan/isSelfScanMode.js';
import { readPersistedSelfScanCheckoutBasketPublicId } from '../../self-scan/selfScanCheckoutPersistence.js';
import { buildCheckoutNavigatePath } from '../../shop/presenters/shopCheckoutPresenter.js';
import { isCustomerCheckoutCartLocked } from '../../promotions/checkoutCartLock.js';
import { isCurrencyCode } from 'pi-kiosk-shared';
import { FIO_PAYMENT_STATUS_POLL_MS, CASH_PAYMENT_STATUS_POLL_MS } from '../../checkout/paymentPollPolicy.js';
import {
  buildQrResumeFromCheckoutSession,
  isTerminalCheckoutSessionStatus,
  resolveActiveQrPayment,
  resolveQrVisibleFromStartQrResponse,
} from '../../checkout/checkoutModeUi.js';
import { writeCheckoutTerminalRecoveryFlash } from '../../checkout/checkoutTerminalRecovery.js';
import {
  clearCheckoutCashCollectResult,
  readCheckoutCashCollectResult,
  writeCheckoutCashCollectResult,
} from '../../checkout/checkoutCashCollectStorage.js';
import {
  clearCheckoutFalseCollectForce,
  clearCheckoutInFlowStep,
  markCheckoutFalseCollectForce,
  persistCheckoutInFlowStepClearingFalseCollect,
  persistCheckoutLeaveOffQrStep,
  persistCheckoutQrDemoteToPayment,
  readCheckoutFalseCollectForce,
  readCheckoutInFlowStep,
  shouldSuppressQrAutoResumeFromInFlowStep,
  writeCheckoutInFlowStep,
} from '../../checkout/checkoutInFlowStepStorage.js';
import {
  resolveMobileCheckoutRails,
} from '../../checkout/checkoutPaymentPolicy.js';
import { resolveCheckoutSalesPointIdFromSession } from '../../checkout/checkoutSalesPointId.js';
import {
  resolveCashCollectShellOwnership,
  resolveCashRailState,
  shouldClearStaleCashCollectResult,
} from '../../checkout/resolveCashCollectShellOwnership.js';
import { shouldShowPayOnSpotSlaNotice } from '../../checkout/payOnSpotSlaNoticePolicy.js';
import { rememberCheckoutMode } from '../../checkout/checkoutModePreference.js';
import {
  buildCheckoutModeForCollectPatch,
} from '../../checkout/checkoutCollectHelpers.js';
import {
  useGatewayPaymentAvailable,
  useWalletPaymentsFeatureEnabled,
  resolveVisibleCustomerPaymentMethods,
  fetchCustomerAccountDailyCashRemaining,
} from '../../checkout/paymentProvidersApi.js';
import {
  fetchGatewayMethods,
  isGatewayMethodsPayable,
  resolveCustomerPaymentStartErrorMessage,
  type GatewayMethodOption,
  type GatewayMethodsFetchResult,
} from '../../checkout/gatewayMethodsApi.js';
import { resolveGatewayProviderSlotId } from '../../checkout/resolveGatewayProviderSlotId.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import {
  resolveCollectPickupPoints,
  useKioskPickupPoints,
} from '../../checkout/pickupPointsApi.js';
import type { PickupSlotSelection } from '../../checkout/PickupSlotPicker.js';
import { useShopKioskById, useShopProducts } from '../../shop/shopApi.js';
import { resetShopCart } from '../../shop/cart/resetShopCart.js';
import {
  reVerifyAfterExpiry,
  type ReVerifyAfterExpiryResult,
} from '../../shop/cart/cartRevalidation.js';
import type { ShopCartLine } from '../../shop/types.js';
import { useShopScheduledPickupEnabled, useShopTenantOpsMode } from '../../shop/hooks/useShopScheduledPickup.js';
import { buildMixedCartRefundTermLines } from '../../checkout/presenters/buildMixedCartRefundTermLines.js';
import type { MixedCartLineTermInput } from '../../checkout/presenters/mixedCartRefundTermsPresenter.js';
import {
  isSalesPointCapabilityStale,
  isSelfCollectOnlyGate,
  resolveAllowedPickupHandoffModes,
  resolveAvailableCheckoutModes,
  resolveCollectPayloadForCheckout,
  resolveDefaultCollectSelection,
  resolveDefaultHandoffMode,
  resolveAllowedPickupHandoffModesForJourney,
  resolveEffectiveHandoffMode,
  resolveEffectiveCollectSelection,
  resolveEffectiveJourneyMode,
  resolveEffectivePickupSlot,
  resolveInitialProductCheckoutStep,
  resolveRestoredProductCheckoutStep,
  resolveLockedCollectTimingForMode,
  shouldBlockCollectContinue,
  shouldInvalidateCheckoutOnSalesPointChange,
  shouldPromoteFalseCollectRecovery,
  scheduleFalseCollectRecoveryPromote,
  type CustomerPaymentMethodId,
  type ProductCheckoutStep,
} from '../../shop/presenters/shopCheckoutPresenter.js';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { useCustomerErrorHandler } from '../../../shared/hooks/useCustomerErrorHandler.js';
import { ApiError } from '../../../shared/api/ApiClient.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import { donationErrorMessage } from '../../../shared/api/errorCodes.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import { useAccountMemberships } from '../../account/accountApi.js';
import { useCustomerSurfaceEntitlement } from '../../entitlements/customerEntitlementGuards.js';
import { savePromoReceiptSnapshot } from '../../checkout/checkoutPromoReceiptSnapshot.js';
import { emitPaymentSubmitted, emitRetailOrderAbandoned } from '../../shop/shopAnalyticsMetadata.js';
import { useCheckoutPricingPreview } from '../../checkout/hooks/useCheckoutPricingPreview.js';
import {
  buildCheckoutIncentiveSurfaceFromHookDeps,
  resolveCheckoutIncentiveConflict,
} from '../../checkout/resolveCheckoutIncentiveSurface.js';
import { resolveCheckoutPaymentIncentives } from '../../checkout/resolveCheckoutPaymentIncentives.js';
import { useCustomerPromoExclusiveConflict } from '../../promotions/useCustomerPromoExclusiveConflict.js';
import { clearCustomerTypedPromoChip } from '../../promotions/customerTypedPromoChipClear.js';
import { removePromoCode } from '../../promotions/promoCodeApplyApi.js';
import { usePromoEvents } from '../../promotions/promoRewardsApi.js';
import { resolveCashCollectOrderNumber } from '../../checkout/resolveCashCollectOrderNumber.js';
import { resolveCustomerTenantDisplayName } from '../../checkout/resolveCustomerTenantDisplayName.js';
import { fetchPublicCustomerTenants } from '../../tenant-select/publicTenantApi.js';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { usePublicDirectorySseConnected } from '../../shops/hooks/PublicDirectorySseProvider.js';
import { buildCustomerPromoExclusiveRemoveIdempotencyKey } from '../../promotions/promoIdempotencyKey.js';
import {
  SIMPLE_EMAIL_REGEX,
  type ProductPaymentRailUiStep,
  resolvePaymentMethodTestId,
} from '../phoneFirstProductCheckoutUtils.js';
import {
  applyBounceToCollectForMetadataRequired,
  runPersistCollectBeforePay,
  tryBounceOnCollectMetadataRequired,
} from './phoneFirstCheckoutCollectPayHelpers.js';

export type PhoneFirstCheckoutActiveStep =
  | 'missing-session'
  | 'qr'
  | 'collect'
  | 'payment'
  | 'cash-collect';

/**
 * G2 — cart/org unmount vs StrictMode remount / in-place QR↔payment step change.
 * Stale leave microtasks no-op when a newer checkout effect epoch is live.
 */
let checkoutLeaveOffQrEpoch = 0;

export function usePhoneFirstCheckoutScreen() {
  const { t, i18n } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const api = useApiClient();
  const { apiBaseUrl } = useRuntimeConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { session: authSession } = useCustomerSession();
  const directorySseConnected = usePublicDirectorySseConnected();
  const directoryRealtime = { directorySseConnected };
  const [params] = useSearchParams();
  const sessionIdParam = params.get('sessionId');
  const sessionIdFromUrl =
    sessionIdParam !== null && sessionIdParam.length > 0 ? sessionIdParam : null;

  // G2 — prefer live active profile key, then base (mutations). Matches
  // useGetCheckoutSession('active') so soft remount sees known-unusable collect.
  const readCachedCheckoutSession = (): CheckoutSessionDTO | undefined => {
    if (sessionIdFromUrl === null) {
      return undefined;
    }
    return readCachedCheckoutSessionFromQueryClient(
      queryClient,
      tenantCode,
      sessionIdFromUrl,
      'active',
    );
  };

  const applyRestoredCheckoutStep = (
    sessionId: string,
    restored: {
      readonly step: ProductCheckoutStep;
      readonly rewriteInFlowTo: 'collect' | 'payment' | null;
      readonly falseCollectForceAction: 'mark' | 'clear' | 'keep';
    },
  ): void => {
    if (restored.rewriteInFlowTo === 'collect') {
      writeCheckoutInFlowStep(sessionId, 'collect');
    } else if (restored.rewriteInFlowTo === 'payment') {
      writeCheckoutInFlowStep(sessionId, 'payment');
    }
    if (restored.falseCollectForceAction === 'mark') {
      markCheckoutFalseCollectForce(sessionId);
    } else if (restored.falseCollectForceAction === 'clear') {
      clearCheckoutFalseCollectForce(sessionId);
    }
    setCheckoutStep(restored.step);
  };

  const [qr, setQr] = useState<QRPaymentResponseDTO | null>(null);
  const [gateway, setGateway] = useState<GatewayPaymentResponseDTO | null>(null);
  const [pendingQr, setPendingQr] = useState<QRPaymentResponseDTO | null>(null);
  const paymentMethodViewedSessionRef = useRef<string | null>(null);
  // The post-Phase-0 `CustomerSession` no longer caches the customer's email
  // (Option A — global Customer + refresh-cookie session); the email is
  // collected at payment start.
  const userEditedEmailRef = useRef(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [paymentError, setPaymentErrorState] = useState<string | null>(null);
  const setPaymentError = useCallback(
    (value: string | null): void => {
      if (value === null) {
        setPaymentErrorState(null);
        return;
      }
      setPaymentErrorState(
        toCustomerFacingErrorMessage(value, {
          fallback: t('checkout.paymentStartFailed'),
        }),
      );
    },
    [t],
  );
  const [paymentRailUiStep, setPaymentRailUiStep] = useState<ProductPaymentRailUiStep>('primary');
  const [selectedPaymentMethodOverride, setSelectedPaymentMethodOverride] =
    useState<CustomerPaymentMethodId | null>(null);
  const [gatewayMethodOptions, setGatewayMethodOptions] = useState<readonly GatewayMethodOption[]>(
    [],
  );
  const [gatewayMethodsResponse, setGatewayMethodsResponse] =
    useState<GatewayMethodsFetchResult | null>(null);
  const [gatewayMethodsLoading, setGatewayMethodsLoading] = useState(false);
  const [gatewayStartingMethodId, setGatewayStartingMethodId] = useState<string | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<ProductCheckoutStep>(() => {
    const cached = readCachedCheckoutSession();
    if (sessionIdFromUrl !== null) {
      const persisted = readCheckoutInFlowStep(sessionIdFromUrl);
      if (persisted !== null) {
        const restored = resolveRestoredProductCheckoutStep({
          persistedInFlowStep: persisted,
          purposeType: cached?.purposeType ?? 'PRODUCT',
          collect: cached?.collect,
          sessionStatus: cached?.status,
          falseCollectForce: readCheckoutFalseCollectForce(sessionIdFromUrl),
          fulfillmentMode: cached?.fulfillmentMode,
        });
        if (restored.rewriteInFlowTo === 'collect') {
          writeCheckoutInFlowStep(sessionIdFromUrl, 'collect');
        } else if (restored.rewriteInFlowTo === 'payment') {
          writeCheckoutInFlowStep(sessionIdFromUrl, 'payment');
        }
        if (restored.falseCollectForceAction === 'mark') {
          markCheckoutFalseCollectForce(sessionIdFromUrl);
        } else if (restored.falseCollectForceAction === 'clear') {
          clearCheckoutFalseCollectForce(sessionIdFromUrl);
        }
        return restored.step;
      }
    }
    if (cached === undefined) {
      return sessionIdFromUrl === null ? 'payment' : 'collect';
    }
    return resolveInitialProductCheckoutStep({
      purposeType: cached.purposeType,
      collect: cached.collect,
      sessionStatus: cached.status,
      fulfillmentMode: cached.fulfillmentMode,
    });
  });
  const [collectSelection, setCollectSelection] = useState<SessionMetadataCollect | null>(() => {
    return readCachedCheckoutSession()?.collect ?? null;
  });
  const [pickupSlot, setPickupSlot] = useState<PickupSlotSelection | null>(null);
  const [selectedHandoffMode, setSelectedHandoffMode] = useState<PickupHandoffModeV3 | null>(null);
  const [selfCollectConfirmed, setSelfCollectConfirmed] = useState(false);
  /** P5 A4 — post low-stock hold expiry re-verify (cart retained). */
  const [holdExpiryReverify, setHoldExpiryReverify] =
    useState<ReVerifyAfterExpiryResult | null>(null);
  const capabilityRefreshInFlightRef = useRef(false);
  const boundSessionIdForStepRef = useRef<string | null>(null);
  const checkoutStepRef = useRef<ProductCheckoutStep>(checkoutStep);
  useLayoutEffect(() => {
    checkoutStepRef.current = checkoutStep;
  }, [checkoutStep]);

  const setCheckoutStepPersisted = useCallback(
    (step: ProductCheckoutStep): void => {
      if (sessionIdFromUrl !== null && (step === 'collect' || step === 'payment')) {
        // Intentional Back→collect / forward→payment — not cold false-collect.
        persistCheckoutInFlowStepClearingFalseCollect(sessionIdFromUrl, step);
      }
      setCheckoutStep(step);
    },
    [sessionIdFromUrl, setCheckoutStep],
  );

  const startQr = useStartQrPayment();
  const startGateway = useStartGatewayPayment();
  const startCash = useStartCashPayment();
  // G10/MOB-05/T6 — sync-read storage so DTO is held before terminal-recovery effects.
  // R1/G-INIT — cashCollectShellResult gates chrome until poll legitimizes (no bank Hotovost flash).
  const [cashCompleteResult, setCashCompleteResult] =
    useState<CashPaymentStartResponseDTO | null>(() =>
      sessionIdFromUrl !== null ? readCheckoutCashCollectResult(sessionIdFromUrl) : null,
    );
  // R1 — session-scoped sticky: poll once reported awaiting===true for this sessionId+paymentId.
  const [sessionSawAwaitingCash, setSessionSawAwaitingCash] = useState(false);
  const [sessionSawAwaitingCashBoundKey, setSessionSawAwaitingCashBoundKey] = useState<
    string | null
  >(null);
  const updateCollect = useUpdateCheckoutSessionCollect();
  const sessionQuery = useGetCheckoutSession(sessionIdFromUrl, 'active');
  const memberships = useAccountMemberships(authSession !== null);
  const publicTenantsQuery = useQuery({
    queryKey: customerQueryKeys.publicCustomerTenants(apiBaseUrl),
    queryFn: ({ signal }) => fetchPublicCustomerTenants(apiBaseUrl, signal),
    staleTime: 300_000,
    enabled: apiBaseUrl.trim().length > 0,
  });
  const surfaceEntitlement = useCustomerSurfaceEntitlement();
  const analytics = useCustomerAnalytics();

  const checkoutSession = sessionQuery.data ?? null;
  const resumedQrFromSession = useMemo(
    () =>
      checkoutSession !== null
        ? buildQrResumeFromCheckoutSession(checkoutSession)
        : null,
    [checkoutSession],
  );
  const [suppressAutoResumeQr, setSuppressAutoResumeQr] = useState(false);
  // Persist-aware suppress: restoreLeft remount clears React suppressAutoResumeQr, but
  // in-flow step 'collect'|'payment' must still block AWAITING_PAYMENT auto-QR (live MOB-05).
  const inFlowStepForQr =
    sessionIdFromUrl !== null ? readCheckoutInFlowStep(sessionIdFromUrl) : null;
  // User leave / in-flow suppress only — never COMPLETED. Resume-only shells
  // (pendingQr / resumedQrFromSession) must stay mounted for guest recovery;
  // explicit continue / exit-from-paid clears qr|pending|session sources.
  const userSuppressAutoResumeQr =
    suppressAutoResumeQr ||
    shouldSuppressQrAutoResumeFromInFlowStep(inFlowStepForQr);
  const activeQrPayment = resolveActiveQrPayment({
    qr,
    pendingQr,
    resumedQrFromSession,
    suppressAutoResumeQr: userSuppressAutoResumeQr,
  });
  // When resume is live and not user-suppressed, durable-hold via pendingQr
  // so COMPLETED session-cache clear does not unmount the QR shell (G1).
  useEffect(() => {
    if (userSuppressAutoResumeQr) {
      return;
    }
    if (qr !== null) {
      return;
    }
    if (pendingQr !== null) {
      return;
    }
    if (resumedQrFromSession === null) {
      return;
    }
    // Defer setState — react-hooks/set-state-in-effect forbids sync setState in effect body.
    queueMicrotask(() => {
      setPendingQr(resumedQrFromSession);
    });
  }, [userSuppressAutoResumeQr, qr, pendingQr, resumedQrFromSession]);
  // Parent poll only when no live QR shell (start-qr, pending, or session resume).
  // QrPaymentView owns the same query key while activeQrPayment is mounted.
  const cashPaymentId = cashCompleteResult?.paymentId ?? null;
  const journeyPaymentId =
    qr?.paymentId ??
    pendingQr?.paymentId ??
    gateway?.paymentId ??
    checkoutSession?.paymentId ??
    cashPaymentId ??
    null;
  // G3 — fast poll while session may cold-resume into cash-collect (before status settles).
  // paymentId present is enough: BE maps PAYMENT_INITIATED → AWAITING_PAYMENT, but first
  // paint can still expose OPEN briefly; do not gate the hint solely on status string.
  const cashResumeSessionStatus = checkoutSession?.status;
  const cashResumePollHint =
    cashCompleteResult === null &&
    (checkoutSession?.paymentId?.trim() ?? '').length > 0 &&
    (cashResumeSessionStatus === 'AWAITING_PAYMENT' || cashResumeSessionStatus === 'OPEN') &&
    activeQrPayment === null &&
    gateway === null &&
    qr === null &&
    pendingQr === null;
  const cashCollectStepActive = cashCompleteResult !== null || cashResumePollHint;
  const journeyPaymentStatusPollMs = cashCollectStepActive
    ? CASH_PAYMENT_STATUS_POLL_MS
    : FIO_PAYMENT_STATUS_POLL_MS;
  const journeyPaymentStatus = usePaymentStatus(journeyPaymentId, journeyPaymentStatusPollMs, {
    enabled: journeyPaymentId !== null && (activeQrPayment === null || cashCollectStepActive),
  });
  const paymentStatusSettled =
    !journeyPaymentStatus.isLoading && !journeyPaymentStatus.isFetching;
  // G1 — optimistic undefined-awaiting must not mount cash-collect (bank cold-remount
  // must not flash cash-waiting while poll unsettled). cashResumePollHint still
  // drives fast poll only; synthesize/hydrate require authoritative awaiting===true.
  const cashColdResumePending =
    cashResumePollHint &&
    paymentStatusSettled &&
    journeyPaymentStatus.data?.awaitingCashConfirmation === true;
  const paymentStatusCompleted = journeyPaymentStatus.data?.status === 'COMPLETED';
  // G5 — paid banner only when poll is COMPLETED and not still awaiting staff cash.
  // Rare API inconsistency: session COMPLETED + tx AWAITING_CASH_CONFIRMATION can yield
  // status COMPLETED with awaitingCashConfirmation true — must not show paid.
  // Staff confirm: awaiting → false + COMPLETED → paid. Poll COMPLETED+awaiting false
  // wins even if cashCompleteResult still has awaitingCashConfirmation true.
  const cashPaymentConfirmed =
    paymentStatusCompleted &&
    journeyPaymentStatus.data?.awaitingCashConfirmation !== true;
  // R1 — sticky awaiting===true for current sessionId+paymentId (render-time adjust; no effect).
  const sessionSawAwaitingCashKey =
    sessionIdFromUrl !== null && journeyPaymentId !== null
      ? `${sessionIdFromUrl}:${journeyPaymentId}`
      : null;
  if (sessionSawAwaitingCashBoundKey !== sessionSawAwaitingCashKey) {
    setSessionSawAwaitingCashBoundKey(sessionSawAwaitingCashKey);
    setSessionSawAwaitingCash(
      journeyPaymentStatus.data?.awaitingCashConfirmation === true,
    );
  } else if (
    !sessionSawAwaitingCash &&
    journeyPaymentStatus.data?.awaitingCashConfirmation === true
  ) {
    setSessionSawAwaitingCash(true);
  }
  // R2 — cash DTO paymentId vs journey/session (settle-wipe keep-until-continue).
  const cashDtoPaymentIdTrimmed = cashCompleteResult?.paymentId?.trim() ?? '';
  const checkoutSessionPaymentIdTrimmed = checkoutSession?.paymentId?.trim() ?? '';
  const journeyPaymentIdTrimmed = journeyPaymentId?.trim() ?? '';
  const cashDtoMatchesSessionPaymentId =
    cashDtoPaymentIdTrimmed.length > 0 &&
    checkoutSessionPaymentIdTrimmed.length > 0 &&
    cashDtoPaymentIdTrimmed === checkoutSessionPaymentIdTrimmed;
  const cashDtoMatchesJourneyPaymentId =
    cashDtoPaymentIdTrimmed.length > 0 &&
    journeyPaymentIdTrimmed.length > 0 &&
    cashDtoPaymentIdTrimmed === journeyPaymentIdTrimmed;
  const cashDtoPaymentIdsMatchJourney =
    cashDtoMatchesSessionPaymentId || cashDtoMatchesJourneyPaymentId;
  // G3/G15 — Stage 1 mandate: continue-without-paid while staff awaits is **only**
  // persisted `collect.selfCollectConfirmedAtKiosk` (kiosk session mint), never the
  // local collect-step checkbox. Other self-pickup / handoff modes require poll → COMPLETED.
  // Never allow during cold-resume hydration: null `cashCompleteResult` made
  // `?.awaitingCashConfirmation !== true` truthy before poll settled.
  const kioskSelfCollectAttested =
    checkoutSession?.collect?.selfCollectConfirmedAtKiosk === true;
  const cashExplicitlyNotAwaiting =
    cashCompleteResult !== null
      ? cashCompleteResult.awaitingCashConfirmation !== true
      : journeyPaymentStatus.data?.awaitingCashConfirmation === false;
  const allowCashContinueWithoutPaid =
    kioskSelfCollectAttested ||
    (paymentStatusSettled && !cashColdResumePending && cashExplicitlyNotAwaiting);
  const tenantDisplayName = useMemo(() => {
    const membershipRow = memberships.data?.memberships?.find(
      (row) => row.tenantCode === tenantCode,
    );
    const publicRow = publicTenantsQuery.data?.find((row) => row.code === tenantCode);
    return resolveCustomerTenantDisplayName({
      tenantCode,
      membershipTenantName: membershipRow?.tenantName,
      publicTenantName: publicRow?.name,
    });
  }, [memberships.data?.memberships, publicTenantsQuery.data, tenantCode]);
  // cashCollectShellResult is declared after visiblePaymentMethods (G2 cashRailState gate).
  const cartResetAfterCompletedRef = useRef(false);
  const paymentSubmittedRef = useRef(false);
  const retailAbandonedEmittedRef = useRef(false);
  const trackRef = useRef(analytics.track);
  const checkoutSessionRef = useRef(checkoutSession);
  const collectRef = useRef<SessionMetadataCollect | null | undefined>(
    checkoutSession?.collect,
  );
  useLayoutEffect(() => {
    trackRef.current = analytics.track;
    checkoutSessionRef.current = checkoutSession;
    collectRef.current = checkoutSession?.collect;
  }, [analytics.track, checkoutSession]);

  const paymentAnalyticsFlow =
    checkoutSession?.purposeType === 'DONATION' ? 'DONATION' : 'RETAIL';
  const { handleError: logCheckoutError, reportVisibleError } = useCustomerErrorHandler(
    'checkout.product',
    {
      track: analytics.track,
      flow: paymentAnalyticsFlow,
      screenName: 'checkout_product',
      screen: 'checkout_product',
    }
  );
  const showPaymentError = useCallback(
    (message: string, operation?: string, error?: unknown): void => {
      const safeMessage = toCustomerFacingErrorMessage(message, {
        fallback: t('checkout.paymentStartFailed'),
      });
      if (error !== undefined) {
        logCheckoutError(error, operation);
      } else {
        reportVisibleError(safeMessage, operation);
      }
      setPaymentError(safeMessage);
    },
    [logCheckoutError, reportVisibleError, setPaymentError, t],
  );
  const awaitingPaymentOnServer =
    checkoutSession?.status === 'AWAITING_PAYMENT' && !paymentStatusCompleted;
  const salesPointId = resolveCheckoutSalesPointIdFromSession(checkoutSession);
  useEffect(() => {
    if (cartResetAfterCompletedRef.current) {
      return;
    }
    const sessionCompleted = checkoutSession?.status === 'COMPLETED';
    const paymentCompleted = journeyPaymentStatus.data?.status === 'COMPLETED';
    if (!sessionCompleted && !paymentCompleted) {
      return;
    }
    if (salesPointId === null || salesPointId <= 0) {
      return;
    }
    cartResetAfterCompletedRef.current = true;
    resetShopCart({
      tenantCode,
      kioskId: salesPointId,
      source: 'journey-payment-completed',
    });
  }, [
    checkoutSession?.status,
    journeyPaymentStatus.data?.status,
    salesPointId,
    tenantCode,
  ]);
  const kioskInfo = useShopKioskById(salesPointId);
  const shopProductsQuery = useShopProducts(salesPointId);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setHoldExpiryReverify(null);
    }, 0);
    return () => {
      window.clearTimeout(handle);
    };
  }, [checkoutSession?.lowStockReservationExpiresAt, checkoutSession?.id]);

  const handleLowStockReservationExpired = useCallback(() => {
    const expiresAt = checkoutSession?.lowStockReservationExpiresAt ?? null;
    const sessionLines = checkoutSession?.lines ?? [];
    const sessionCurrency = checkoutSession?.currency ?? null;
    const cartLines = mapCheckoutSessionLinesToShopCartLines(sessionLines);

    void shopProductsQuery.refetch().then((refetched) => {
      const products = refetched.data ?? shopProductsQuery.data;
      const result = reVerifyAfterExpiry(cartLines, products, expiresAt, {
        sessionCurrency,
      });
      setHoldExpiryReverify(result);
    });
  }, [
    checkoutSession?.currency,
    checkoutSession?.lines,
    checkoutSession?.lowStockReservationExpiresAt,
    shopProductsQuery,
  ]);

  const mixedCartRefundTermLines: readonly MixedCartLineTermInput[] = useMemo(
    () =>
      buildMixedCartRefundTermLines(
        checkoutSession?.lines ?? [],
        shopProductsQuery.data
      ),
    [checkoutSession?.lines, shopProductsQuery.data]
  );
  const checkoutIncentiveSurface = useMemo(
    () =>
      buildCheckoutIncentiveSurfaceFromHookDeps({
        memberships: memberships.data,
        loyaltyProgramEntitled: surfaceEntitlement.isBlockEntitled('loyalty_program'),
        promotionsProgramEntitled: surfaceEntitlement.isBlockEntitled('promotions_program'),
        commerceCapabilitiesPromotionsProgramEnabled:
          kioskInfo.data?.promotionsProgramEnabled,
        commerceConfigJson: kioskInfo.data?.commerceConfigJson ?? null,
      }),
    [
      kioskInfo.data?.commerceConfigJson,
      kioskInfo.data?.promotionsProgramEnabled,
      memberships.data,
      surfaceEntitlement,
    ],
  );
  const {
    promotionsEnabled,
    loyaltyEnabled,
    promotionsProgramEnabled,
    promoPricingEnabled,
    showTypedPromoBlock,
    showPromoPreview,
    showLoyaltyPreview,
    incentiveProgramsEnabled,
  } = checkoutIncentiveSurface;
  const refetchKioskCapabilities = kioskInfo.refetch;
  const pickupPointsQuery = useKioskPickupPoints(salesPointId, directoryRealtime);
  const collectPickupResolved = useMemo(
    () =>
      resolveCollectPickupPoints({
        salesPointId,
        queryPoints: pickupPointsQuery.data,
        isError: pickupPointsQuery.isError,
        isLoading: pickupPointsQuery.isLoading,
        fallbackName: kioskInfo.data?.name ?? null,
      }),
    [
      salesPointId,
      kioskInfo.data?.name,
      pickupPointsQuery.data,
      pickupPointsQuery.isError,
      pickupPointsQuery.isLoading,
    ],
  );
  const pickupPoints = collectPickupResolved.points;
  const walletPaymentsEnabled = useWalletPaymentsFeatureEnabled();
  const scheduledPickupProbeInput = {
    tenantCode,
    kioskId: salesPointId,
    pickupPointId: pickupPoints[0]?.id,
    pickupPointsLoaded: pickupPoints.length > 0,
  };
  const scheduledPickupEnabled = useShopScheduledPickupEnabled(scheduledPickupProbeInput);
  const tenantOpsMode = useShopTenantOpsMode(scheduledPickupProbeInput);
  const availableCheckoutModes = useMemo(
    () => resolveAvailableCheckoutModes(kioskInfo.data ?? null, scheduledPickupEnabled),
    [kioskInfo.data, scheduledPickupEnabled],
  );
  const effectiveJourneyMode = resolveEffectiveJourneyMode(
    checkoutSession?.checkoutSubMode ??
      checkoutSession?.checkoutMode?.selectedMode ??
      null,
    availableCheckoutModes,
  );
  const lockedCollectTiming = resolveLockedCollectTimingForMode(effectiveJourneyMode);
  const capabilityHandoffModes = useMemo(
    () => resolveAllowedPickupHandoffModes(kioskInfo.data ?? null),
    [kioskInfo.data],
  );
  const allowedPickupHandoffModes = useMemo(
    () =>
      resolveAllowedPickupHandoffModesForJourney(
        effectiveJourneyMode,
        capabilityHandoffModes,
      ),
    [capabilityHandoffModes, effectiveJourneyMode],
  );
  const defaultHandoffMode = useMemo(
    () =>
      resolveDefaultHandoffMode({
        effectiveJourneyMode,
        kioskMeta: kioskInfo.data ?? null,
        allowedPickupHandoffModes: capabilityHandoffModes,
      }),
    [capabilityHandoffModes, effectiveJourneyMode, kioskInfo.data],
  );
  const effectiveHandoffMode = resolveEffectiveHandoffMode({
    selectedHandoffMode,
    defaultHandoffMode,
    effectiveJourneyMode,
  });
  const selfCollectOnlyGate = isSelfCollectOnlyGate({
    enabledMobileFirstCheckoutSubModes: availableCheckoutModes,
    rawEnabledMobileFirstCheckoutSubModes: kioskInfo.data?.enabledMobileFirstCheckoutSubModes,
    scheduledPickupEnabled,
    immediateSelfPickupEntitled: surfaceEntitlement.isBlockEntitled('immediate_self_pickup'),
  });
  const selfCollectConfirmAtKiosk = surfaceEntitlement.isBlockEntitled('surface_kiosk');
  const defaultCollectSelection = useMemo(
    () => resolveDefaultCollectSelection(pickupPoints, lockedCollectTiming),
    [lockedCollectTiming, pickupPoints],
  );
  const effectiveCollectSelection = useMemo(
    () =>
      resolveEffectiveCollectSelection({
        collectSelection: collectSelection ?? checkoutSession?.collect ?? null,
        defaultCollectSelection,
        lockedCollectTiming,
      }),
    [checkoutSession?.collect, collectSelection, defaultCollectSelection, lockedCollectTiming],
  );
  const effectivePickupSlot = resolveEffectivePickupSlot(pickupPoints, pickupSlot);
  const selectedCheckoutMode = effectiveJourneyMode;
  const [checkoutHelpOpen, setCheckoutHelpOpen] = useState(false);
  const [slaNoticeDismissed, setSlaNoticeDismissed] = useState(false);
  const gatewayPaymentAvailable = useGatewayPaymentAvailable(kioskInfo.data?.paymentRailsMobile);

  const [paymentBoundSessionId, setPaymentBoundSessionId] = useState<string | null>(sessionIdFromUrl);
  if (paymentBoundSessionId !== sessionIdFromUrl) {
    setPaymentBoundSessionId(sessionIdFromUrl);
    setQr(null);
    setGateway(null);
    setPaymentError(null);
    setPendingQr(null);
    setSuppressAutoResumeQr(false);
    setPaymentRailUiStep('primary');
    setGatewayMethodOptions([]);
    setGatewayMethodsResponse(null);
    setGatewayMethodsLoading(false);
    setGatewayStartingMethodId(null);
    setSelectedPaymentMethodOverride(null);
    // Session-id rebind: drop prior session cash React state (storage keyed by id).
    setCashCompleteResult(null);
  }

  const invalidateCheckoutSession = useCallback(async (sessionId: string): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: customerQueryKeys.checkoutSession(tenantCode, sessionId),
    });
  }, [queryClient, tenantCode]);

  useEffect(() => {
    if (salesPointId !== null && salesPointId > 0) {
      rememberSalesPointIdForTenant(tenantCode, salesPointId);
    }
  }, [salesPointId, tenantCode]);

  // Bind the analytics session to the checkout session UUID so both
  // surfaces share one identifier (plan §5 / §9.4.1). This must run
  // before the first `track()` call from this screen.
  useEffect(() => {
    if (sessionIdFromUrl !== null && sessionIdFromUrl.length > 0) {
      analytics.bindSessionId(sessionIdFromUrl);
    }
  }, [analytics, sessionIdFromUrl]);

  useEffect(() => {
    if (sessionIdFromUrl !== null) {
      const flow = checkoutSession?.purposeType === 'DONATION' ? 'DONATION' : 'RETAIL';
      analytics.track({
        eventName: 'screen_viewed',
        flow,
        screenName: 'checkout_product',
        metadata: {},
      });
    }
  }, [analytics, checkoutSession?.purposeType, sessionIdFromUrl]);

  useEffect(() => {
    return () => {
      if (paymentSubmittedRef.current || retailAbandonedEmittedRef.current) {
        return;
      }
      const session = checkoutSessionRef.current;
      if (session === null || session.purposeType === 'DONATION') {
        return;
      }
      if (session.status === 'COMPLETED') {
        return;
      }
      const lineCount = session.lines.length;
      if (lineCount <= 0) {
        return;
      }
      emitRetailOrderAbandoned(
        trackRef.current,
        'checkout_product',
        lineCount,
        session.amountMinor,
      );
      retailAbandonedEmittedRef.current = true;
    };
  }, []);

  const subtitleAmountMinor = useMemo<number>(() => {
    if (checkoutSession !== null) {
      return checkoutSession.amountMinor;
    }
    return 0;
  }, [checkoutSession]);
  const subtitleCurrency = resolveDisplayCurrencyFromSession(
    checkoutSession?.currency,
    kioskInfo.data?.acceptedCurrencies?.[0],
  );

  const showCheckoutCurrencyPicker = canShowCheckoutCurrencyPicker(kioskInfo.data);
  const checkoutCurrencyOptions = resolveAcceptedCheckoutCurrencies(kioskInfo.data);
  /** Self-Scan handoff: query parks + sessionStorage bind + durable session fields (AC-11). */
  const selfScanCheckoutHandoff =
    isSelfScanCheckoutSearch(location.search) ||
    readPersistedSelfScanCheckoutBasketPublicId(tenantCode, sessionIdFromUrl) !== null;
  const sellerAssistedCheckout = isSellerAssistedCheckoutOrigin(checkoutSession);
  const sellerAssistedDisplayLines: readonly CheckoutSessionDisplayLine[] = useMemo(
    () =>
      resolveSellerAssistedDisplayLines({
        displayLines: checkoutSession?.displayLines,
        lines: checkoutSession?.lines,
        products: shopProductsQuery.data ?? null,
      }),
    [checkoutSession?.displayLines, checkoutSession?.lines, shopProductsQuery.data],
  );
  const sellerAssistedMerchantName = tenantDisplayName.trim().length > 0 ? tenantDisplayName : null;
  const sellerAssistedSalesPointName = kioskInfo.data?.name?.trim() || kioskInfo.data?.code?.trim() || null;
  const currencyLocked = isCheckoutCurrencyLocked({
    paymentId: checkoutSession?.paymentId,
    hasActiveQr: activeQrPayment !== null || pendingQr !== null,
    hasGateway: gateway !== null,
    awaitingPaymentOnServer,
    cartLocked: isCustomerCheckoutCartLocked({
      awaitingPaymentOnServer,
      hasActiveQrPayment: activeQrPayment !== null,
      hasActiveGatewayPayment: gateway !== null,
    }),
    selfScanCheckoutHandoff,
    collectLockedAt: checkoutSession?.collectLockedAt,
    selfScanBound: checkoutSession?.selfScanBound,
    sellerAssistedCheckout,
  });
  const createCheckoutSessionForCurrency = useCreateCheckoutSession();
  const [currencySwitchPending, setCurrencySwitchPending] = useState(false);
  const [currencySwitchError, setCurrencySwitchError] = useState<string | null>(null);
  const [shippingContinuePending, setShippingContinuePending] = useState(false);

  const handleCheckoutCurrencySelect = useCallback(
    async (nextCurrency: CurrencyCode): Promise<void> => {
      // G1 / AC-11: never generic recreate after Self-Scan handoff (frozen prices + bound session).
      // SA gateway phone review: staff basket SoT — never customer recreate (G14 / AC-04).
      if (
        checkoutSession === null ||
        currencyLocked ||
        selfScanCheckoutHandoff ||
        sellerAssistedCheckout ||
        salesPointId === null
      ) {
        return;
      }
      const current = resolveDisplayCurrencyFromSession(checkoutSession.currency);
      if (current === nextCurrency) {
        return;
      }
      setCurrencySwitchError(null);
      setCurrencySwitchPending(true);
      writeStickyCheckoutCurrency(salesPointId, nextCurrency);
      try {
        // Shop/retail only: map ids+qty (server catalog price). Self-Scan path is locked above.
        const lines =
          checkoutSession.lines.length > 0
            ? checkoutSession.lines.map((line) => ({
                productId: line.productId,
                variantId: line.variantId,
                quantity: line.quantity,
              }))
            : undefined;
        const session = await createCheckoutSessionForCurrency.mutateAsync({
          currency: nextCurrency,
          purposeType: checkoutSession.purposeType,
          flowType: checkoutSession.flowType,
          entryChannel: checkoutSession.entryChannel,
          kioskId: salesPointId,
          checkoutSubMode: checkoutSession.checkoutSubMode ?? undefined,
          collect: checkoutSession.collect ?? undefined,
          checkoutMode: checkoutSession.checkoutMode ?? undefined,
          donationProjectCode: checkoutSession.donationProjectCode,
          ...(lines !== undefined
            ? { lines }
            : { amountMinor: checkoutSession.amountMinor }),
        });
        navigate(
          buildCheckoutNavigatePath(tenantCode, session.id, {
            preserveFromSearch: location.search,
          }),
          { replace: true },
        );
      } catch (err) {
        setCurrencySwitchError(
          err instanceof Error && err.message.trim().length > 0
            ? err.message
            : t('checkout.currency.switchFailed'),
        );
      } finally {
        setCurrencySwitchPending(false);
      }
    },
    [
      checkoutSession,
      createCheckoutSessionForCurrency,
      currencyLocked,
      location.search,
      navigate,
      salesPointId,
      selfScanCheckoutHandoff,
      sellerAssistedCheckout,
      t,
      tenantCode,
    ],
  );

  const selectedCheckoutCurrency: CurrencyCode = isCurrencyCode(subtitleCurrency)
    ? subtitleCurrency
    : 'CZK';

  const checkoutPreviewLines = useMemo(
    () =>
      (checkoutSession?.lines ?? []).map((line) => ({
        productId: line.productId,
        variantId: line.variantId,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        lineKey: `${line.productId}:${line.variantId ?? 'n'}`,
      })),
    [checkoutSession?.lines],
  );
  const promoEventsQuery = usePromoEvents(
    authSession?.customerId ?? null,
    promoPricingEnabled,
    directoryRealtime,
  );
  const promoEventNameById = useMemo(
    () =>
      new Map(
        (promoEventsQuery.data ?? []).map((event) => [
          event.id,
          resolveLocalizedName(event.name, event.nameLocales, i18n.language),
        ]),
      ),
    [i18n.language, promoEventsQuery.data],
  );
  const releaseExclusivePromoCode = useCallback(
    async (checkoutSessionId: string): Promise<void> => {
      // Propagate failure — chooseLoyalty awaits success before loyalty-only.
      await removePromoCode(api, {
        checkoutSessionPublicId: checkoutSessionId,
        idempotencyKey: buildCustomerPromoExclusiveRemoveIdempotencyKey(checkoutSessionId),
      });
    },
    [api],
  );
  const promoExclusiveConflict = useCustomerPromoExclusiveConflict({
    stackingMode: 'EXCLUSIVE',
    tenantCode,
    checkoutSessionId: sessionIdFromUrl,
    onReleasePromoCode: releaseExclusivePromoCode,
    onClearTypedPromo: clearCustomerTypedPromoChip,
  });
  const serverActivatedPromoRewardId =
    checkoutSession?.promotions?.activatedPromoRewardId ?? null;
  const serverPromoResolved = checkoutSession != null;
  const pricingPreview = useCheckoutPricingPreview({
    customerId: authSession?.customerId ?? null,
    checkoutSessionId: sessionIdFromUrl,
    items: checkoutPreviewLines,
    promotionsEnabled: promoPricingEnabled,
    loyaltyEnabled,
    activatedPromoRewardId: promoExclusiveConflict.effectiveActivatedPromoRewardId,
    activatedCouponId: promoExclusiveConflict.effectiveActivatedCouponId,
    serverActivatedPromoRewardId,
    serverPromoResolved,
  });
  const checkoutIncentiveConflict = resolveCheckoutIncentiveConflict({
    incentiveProgramsEnabled,
    hasCheckoutSession: checkoutSession !== null,
    isDonationSession: checkoutSession?.purposeType === 'DONATION',
    showExclusiveModal: promoExclusiveConflict.showExclusiveModal,
    mutualExclusion: pricingPreview.data?.mutualExclusion === true,
  });

  const sessionLoading =
    sessionIdFromUrl !== null && checkoutSession === null && sessionQuery.isPending;
  const sessionError =
    sessionIdFromUrl !== null && sessionQuery.isError ? sessionQuery.error : null;
  /**
   * G17 — lock the first observed SA basketVersion; if server advances (staff edit /
   * invalidate), client is stale and pay is blocked.
   */
  const sellerAssistedSessionKey = checkoutSession?.id ?? '';
  const sellerAssistedServerBasketVersion = checkoutSession?.basketVersion;
  const sellerAssistedOrigin = isSellerAssistedCheckoutOrigin(checkoutSession);
  const [sellerAssistedFrozenBasket, setSellerAssistedFrozenBasket] = useState<{
    readonly key: string;
    readonly version: number;
  } | null>(null);
  if (!sellerAssistedOrigin) {
    if (sellerAssistedFrozenBasket !== null) {
      setSellerAssistedFrozenBasket(null);
    }
  } else if (
    typeof sellerAssistedServerBasketVersion === 'number' &&
    Number.isFinite(sellerAssistedServerBasketVersion) &&
    (sellerAssistedFrozenBasket === null ||
      sellerAssistedFrozenBasket.key !== String(sellerAssistedSessionKey))
  ) {
    setSellerAssistedFrozenBasket({
      key: String(sellerAssistedSessionKey),
      version: sellerAssistedServerBasketVersion,
    });
  }
  let sellerAssistedClientBasketVersion: number | null = null;
  if (sellerAssistedOrigin) {
    if (sellerAssistedFrozenBasket?.key === String(sellerAssistedSessionKey)) {
      sellerAssistedClientBasketVersion = sellerAssistedFrozenBasket.version;
    } else if (typeof sellerAssistedServerBasketVersion === 'number') {
      sellerAssistedClientBasketVersion = sellerAssistedServerBasketVersion;
    }
  }
  const sellerAssistedStaleHandoff = isSellerAssistedPaymentBlockedByStaleHandoff({
    session: checkoutSession,
    clientBasketVersion: sellerAssistedClientBasketVersion,
    sessionError,
  });
  const sessionCollect = effectiveCollectSelection;
  const isDonationSession = checkoutSession?.purposeType === 'DONATION';
  const collectPayloadForPersist = resolveCollectPayloadForCheckout({
    effectiveCollectSelection,
    effectivePickupSlot,
    selfCollectOnlyGate,
    selfCollectConfirmed,
  });
  const collectContinueBlocked = shouldBlockCollectContinue({
    collectPayload: collectPayloadForPersist,
    lockedCollectTiming,
    scheduledPickupEnabled,
    effectivePickupSlot,
    selfCollectOnlyGate,
    selfCollectConfirmed,
  });
  const sessionSalesPointId =
    checkoutSession !== null ? resolveCheckoutSalesPointIdFromSession(checkoutSession) : null;
  const salesPointDrift = shouldInvalidateCheckoutOnSalesPointChange({
    sessionSalesPointId,
    currentSalesPointId: salesPointId,
  });
  const activeCheckoutStep: ProductCheckoutStep = salesPointDrift ? 'collect' : checkoutStep;
  const isShippingFulfillment = checkoutSession?.fulfillmentMode === 'SHIPPING';
  const payActionsDisabled =
    sessionLoading ||
    startQr.isPending ||
    startGateway.isPending ||
    gatewayMethodsLoading ||
    gatewayStartingMethodId !== null ||
    updateCollect.isPending ||
    checkoutIncentiveConflict ||
    checkoutSession === null ||
    isTerminalCheckoutSessionStatus(checkoutSession?.status) ||
    paymentStatusCompleted ||
    (kioskInfo.isLoading && kioskInfo.data === undefined) ||
    // SHIPPING sessions omit collect — do not treat null collect as unpaid gate.
    (!isDonationSession &&
      !isShippingFulfillment &&
      activeCheckoutStep === 'payment' &&
      sessionCollect === null) ||
    holdExpiryReverify?.hasBlockingIssues === true ||
    sellerAssistedStaleHandoff;

  // Recovery is resetShopCart + replace-nav + flash toast (no recoverySessionId query).
  const buildShopRecoveryPath = useCallback((): string => {
    const kioskSlug = kioskInfo.data?.code?.trim() ?? '';
    if (kioskSlug.length > 0) {
      return `/${encodeURIComponent(tenantCode)}/${encodeURIComponent(kioskSlug)}`;
    }
    const params = new URLSearchParams();
    if (checkoutSession?.kioskId !== null && checkoutSession?.kioskId !== undefined) {
      params.set('kioskId', String(checkoutSession.kioskId));
    }
    const query = params.toString();
    return `/${encodeURIComponent(tenantCode)}/shop${query.length > 0 ? `?${query}` : ''}`;
  }, [checkoutSession, kioskInfo.data?.code, tenantCode]);

  /** Terminal refuse / CHECKOUT_SESSION_INVALID_STATE → flash then replace-nav to shop. */
  const recoverFromTerminalCheckoutSession = useCallback((sid: string): void => {
    writeCheckoutTerminalRecoveryFlash();
    clearCheckoutInFlowStep(sid);
    clearCheckoutCashCollectResult(sid);
    setQr(null);
    setGateway(null);
    setPendingQr(null);
    setCashCompleteResult(null);
    setSuppressAutoResumeQr(false);
    setPaymentError(null);
    navigate(buildShopRecoveryPath(), { replace: true });
  }, [
    buildShopRecoveryPath,
    navigate,
    setCashCompleteResult,
    setGateway,
    setPaymentError,
    setPendingQr,
    setQr,
    setSuppressAutoResumeQr,
  ]);

  const handleExitPaymentFlow = async (): Promise<void> => {
    if (sessionIdFromUrl === null) {
      setQr(null);
      setGateway(null);
      setPendingQr(null);
      setSuppressAutoResumeQr(false);
      return;
    }
    // QrPaymentView polls the same paymentStatus query key; cache may already be
    // COMPLETED even while parent poll is disabled (activeQrPayment mounted).
    const paymentUiCompleted = journeyPaymentStatus.data?.status === 'COMPLETED';
    const refreshed = await sessionQuery.refetch();
    const sessionStatus = refreshed.data?.status;
    const sessionTerminal = isTerminalCheckoutSessionStatus(sessionStatus);
    // G2: paid / terminal exit never keeps resume banner / Show QR — even when
    // session refetch lags on AWAITING_PAYMENT after bank settlement.
    if (paymentUiCompleted || sessionTerminal) {
      setQr(null);
      setGateway(null);
      setPendingQr(null);
      clearCheckoutCashCollectResult(sessionIdFromUrl);
      setCashCompleteResult(null);
      setSuppressAutoResumeQr(false);
      clearCheckoutInFlowStep(sessionIdFromUrl);
      navigate(buildShopRecoveryPath(), { replace: true });
      return;
    }
    if (sessionStatus === 'AWAITING_PAYMENT') {
      setQr(null);
      setGateway(null);
      const resumed =
        refreshed.data !== undefined
          ? buildQrResumeFromCheckoutSession(refreshed.data)
          : null;
      // Keep pendingQr for payment-step resume banner; suppress so activeQrPayment
      // does not re-enter the QR step (G6 / MOB-05). Prefer the live start-qr
      // DTO (SPAYD / qrVisible) over a session stub so Resume shows the QR again.
      setPendingQr((prev) => {
        if (prev !== null && resolveQrVisibleFromStartQrResponse(prev)) {
          return prev;
        }
        return resumed;
      });
      setSuppressAutoResumeQr(true);
      if (resumed !== null) {
        // Banner on payment step owns the lock copy — avoid duplicate alert / QR trap.
        setPaymentError(null);
      } else {
        showPaymentError(t('checkout.modeLockedPaymentInProgress'), 'exitPaymentFlowLocked');
      }
      return;
    }
    // Other non-awaiting (e.g. OPEN after soft exit): clear local QR/cash shell.
    // Soft OPEN clears cash storage; AWAITING_PAYMENT above does not (MOB-05 remount).
    setQr(null);
    setGateway(null);
    setPendingQr(null);
    clearCheckoutCashCollectResult(sessionIdFromUrl);
    setCashCompleteResult(null);
    setSuppressAutoResumeQr(false);
  };

  /** Latest session after network: refetch result, else `checkoutSessionRef` (layout effect lags). */
  const refetchCheckoutSessionIntoRef = async (): Promise<CheckoutSessionDTO | null> => {
    try {
      const refreshed = await sessionQuery.refetch();
      if (refreshed.data !== undefined) {
        checkoutSessionRef.current = refreshed.data;
      }
      return refreshed.data ?? checkoutSessionRef.current;
    } catch (refetchErr) {
      void refetchErr;
      return checkoutSessionRef.current;
    }
  };

  /**
   * G3: after start-qr, gate on refetch/ref — not hook `checkoutSession`.
   * Nonempty start-qr paymentId always mounts (reusable unpaid). qrNotAvailable
   * only when the DTO hid QR and there is no reusable paymentId.
   */
  const mountLiveStartQrOrDeny = async (
    sid: string,
    res: QRPaymentResponseDTO,
  ): Promise<boolean> => {
    const qrAllowed = resolveQrVisibleFromStartQrResponse(res);
    const latestSession = await refetchCheckoutSessionIntoRef();
    const startQrPaymentId = res.paymentId.trim();
    const sessionPaymentId = latestSession?.paymentId?.trim() ?? '';
    const reusePaymentId =
      startQrPaymentId.length > 0 ? startQrPaymentId : sessionPaymentId;
    const awaiting =
      latestSession?.status === 'AWAITING_PAYMENT' || startQrPaymentId.length > 0;
    if (
      !qrAllowed &&
      startQrPaymentId.length === 0 &&
      (!awaiting || reusePaymentId.length === 0)
    ) {
      showPaymentError(t('qrPayment.qrNotAvailable'), 'startQrAccessDenied');
      return false;
    }
    const qrForStep: QRPaymentResponseDTO =
      qrAllowed || startQrPaymentId.length > 0
        ? res
        : { ...res, paymentId: reusePaymentId };
    // G4 — leaving cash for QR (start / unpaid reuse); keep MOB-05 storage only for true cash awaiting.
    clearCheckoutCashCollectResult(sid);
    setCashCompleteResult(null);
    setPendingQr(qrForStep);
    setQr(qrForStep);
    writeCheckoutInFlowStep(sid, 'qr');
    setSuppressAutoResumeQr(false);
    setPaymentError(null);
    return true;
  };

  /**
   * Shared COLLECT_METADATA_REQUIRED recovery — land on collect (primary QR,
   * gateway, cash, unpaid-reuse Show QR). Do not toast-only on payment.
   */
  const bounceToCollectForMetadataRequired = (
    analyticsSource: string,
    err?: unknown,
  ): void => {
    applyBounceToCollectForMetadataRequired({
      sessionId: sessionIdFromUrl,
      sessionCollect: checkoutSession?.collect,
      writeCheckoutInFlowStep,
      setCheckoutStep,
      markCheckoutFalseCollectForce,
      clearCheckoutFalseCollectForce,
      showPaymentError,
      message: donationErrorMessage(
        err instanceof ApiError ? err.code : 'COLLECT_METADATA_REQUIRED',
        t('errors.collectMetadataRequired'),
      ),
      analyticsSource,
      err,
    });
  };

  /** Unpaid reuse: POST start-qr and mount live DTO only. Never promote a payload-less stub. */
  const fetchAndApplyLiveStartQr = async (sid: string): Promise<boolean> => {
    // G6: refuse terminal before POST (same recovery as handleQr).
    if (
      checkoutSession !== null &&
      isTerminalCheckoutSessionStatus(checkoutSession.status)
    ) {
      recoverFromTerminalCheckoutSession(sid);
      return false;
    }
    try {
      const res = await startQr.mutateAsync({
        sessionId: sid,
        analyticsSessionId: sid,
      });
      if (!(await mountLiveStartQrOrDeny(sid, res))) {
        return false;
      }
      await invalidateCheckoutSession(sid);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TENANT_SWITCH_REDIRECT') {
        return false;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        recoverFromTerminalCheckoutSession(sid);
        return false;
      }
      if (tryBounceOnCollectMetadataRequired(err, bounceToCollectForMetadataRequired, 'startQrUnpaidReuseCollectRequired')) {
        return false;
      }
      const message = resolveCustomerPaymentStartErrorMessage(
        err,
        t('checkout.paymentStartFailed'),
        (key) => t(key),
      );
      showPaymentError(message, 'startQrUnpaidReuse', err);
      return false;
    }
  };

  const handleResumeAwaitingPayment = (): void => {
    const sid = sessionIdFromUrl;
    if (sid === null) {
      return;
    }
    if (pendingQr === null && resumedQrFromSession === null) {
      return;
    }
    // Stay on payment until live DTO arrives (startQr.isPending → preparing, not stub QR).
    void fetchAndApplyLiveStartQr(sid);
  };

  const resumeQrFromSession = async (sessionId: string): Promise<boolean> => {
    const ok = await fetchAndApplyLiveStartQr(sessionId);
    if (!ok) {
      return false;
    }
    paymentSubmittedRef.current = true;
    emitPaymentSubmitted(trackRef.current, 'checkout_product', 'QR', paymentAnalyticsFlow);
    return true;
  };

  const effectiveCheckoutSubMode = selectedCheckoutMode;
  const effectiveCheckoutMode =
    effectiveCheckoutSubMode !== null
      ? buildCheckoutModeForCollectPatch(
          effectiveCheckoutSubMode,
          kioskInfo.data?.commerceConfigJson ?? null,
          effectiveHandoffMode,
        )
      : (checkoutSession?.checkoutMode ?? null);
  const paymentRailsMobile = kioskInfo.data?.paymentRailsMobile ?? {
    bankTransfer: false,
    gateway: false,
  };
  const resolvedRails =
    effectiveCheckoutMode !== null
      ? resolveMobileCheckoutRails({
          purposeType: checkoutSession?.purposeType ?? 'PRODUCT',
          checkoutMode: effectiveCheckoutMode,
          collect: sessionCollect,
          paymentRailsMobile,
          gatewayHealthAvailable: gatewayPaymentAvailable,
        })
      : { bankQr: false, gateway: false };
  const paymentSurfaceReadiness = kioskInfo.data?.paymentSurfaceReadiness;
  const customerRequireVerified = true;
  const qrRailCandidate = resolvedRails.bankQr;
  const readinessMethods = paymentSurfaceReadiness?.methods;
  const mobileQrAllowed =
    qrRailCandidate &&
    (readinessMethods === undefined
      ? true
      : isMethodPayableForCount(readinessMethods.bankTransfer, customerRequireVerified));
  const gatewayRailCandidate =
    resolvedRails.gateway &&
    (readinessMethods === undefined
      ? true
      : isMethodPayableForCount(readinessMethods.gateway, customerRequireVerified));
  const gatewayMethodsProbeCurrency = (() => {
    const raw = checkoutSession?.currency?.trim().toUpperCase() ?? '';
    return raw.length > 0 ? resolveDisplayCurrencyFromSession(raw) : null;
  })();
  const gatewayMethodsProbeEnabled =
    checkoutStep === 'payment' &&
    gatewayRailCandidate &&
    salesPointId !== null &&
    salesPointId > 0 &&
    gatewayMethodsProbeCurrency !== null;
  const gatewayMethodsProbeQuery = useQuery({
    queryKey: customerQueryKeys.gatewayMethods(
      tenantCode,
      salesPointId ?? 0,
      gatewayMethodsProbeCurrency ?? 'PENDING',
      'PRODUCT_PURCHASE',
    ),
    queryFn: async (): Promise<GatewayMethodsFetchResult> => {
      if (salesPointId === null || salesPointId <= 0) {
        throw new Error('Missing sales point for gateway methods probe');
      }
      if (gatewayMethodsProbeCurrency === null) {
        throw new Error('Missing checkout session currency for gateway methods probe');
      }
      return fetchGatewayMethods({
        apiBaseUrl,
        tenantCode,
        salesPointId,
        currency: gatewayMethodsProbeCurrency,
        purpose: 'PRODUCT_PURCHASE',
      });
    },
    enabled: gatewayMethodsProbeEnabled,
    staleTime: 30_000,
    retry: 1,
  });
  const gatewayMethodsPayable = ((): boolean | null => {
    if (!gatewayRailCandidate) {
      return false;
    }
    if (!gatewayMethodsProbeEnabled) {
      return null;
    }
    const probeSettled =
      gatewayMethodsProbeQuery.isSuccess || gatewayMethodsProbeQuery.isError;
    if (!probeSettled) {
      return null;
    }
    if (gatewayMethodsProbeQuery.isError) {
      return false;
    }
    return isGatewayMethodsPayable(gatewayMethodsProbeQuery.data);
  })();
  const gatewayMethodsProbePending =
    gatewayRailCandidate && gatewayMethodsPayable === null;
  const mobileGatewayAllowed =
    gatewayRailCandidate && gatewayMethodsPayable === true;
  const signedInCustomerId = authSession?.customerId ?? null;
  const dailyCashRemainingQuery = useQuery({
    queryKey: customerQueryKeys.customerAccountDailyCashRemaining(
      tenantCode,
      signedInCustomerId ?? 0,
    ),
    queryFn: () => fetchCustomerAccountDailyCashRemaining({ api }),
    enabled: signedInCustomerId != null,
    staleTime: 15_000,
    retry: 1,
  });
  // Self-Scan: keep CASH Stage 1–4 when readiness allows. Customer never
  // self-confirms — staff ConfirmCashPaymentReceivedUseCase completes payment.
  const visiblePaymentMethods = resolveVisibleCustomerPaymentMethods({
    bankQr: mobileQrAllowed,
    gateway: gatewayRailCandidate,
    walletPaymentsEnabled,
    paymentSurfaceReadiness,
    gatewayMethodsPayable,
    salesPointInteractionMode: kioskInfo.data?.salesPointInteractionMode,
    customerAuthenticated: signedInCustomerId != null,
    checkoutAmountMinor: checkoutSession?.amountMinor ?? 0,
    customerAccountDailyCashRemainingMinor:
      dailyCashRemainingQuery.data?.customerAccountDailyCashRemainingMinor ?? null,
    customerAccountDailyCashRemainingResolved: dailyCashRemainingQuery.isSuccess,
  });
  const cashSignInRequired =
    kioskInfo.data?.salesPointInteractionMode === 'CUSTOMER_FACING' &&
    authSession?.customerId == null &&
    paymentSurfaceReadiness != null &&
    paymentSurfaceReadiness.methods.cash !== undefined &&
    isMethodPayableForCount(paymentSurfaceReadiness.methods.cash, true);
  // R2/R5 — readiness null → unknown (not denied). Cold paths need allowed; settle wipe needs denied.
  const cashRailState = resolveCashRailState({
    paymentSurfaceReadiness,
    cashInVisibleMethods: visiblePaymentMethods.includes('CASH'),
  });
  // G1 — synthesize only when poll authoritatively reports awaitingCashConfirmation
  // (mirrors hydrate). Undefined/unsettled awaiting must not mount cash chrome.
  // Bare COMPLETED is bank-transfer / FIO settle — do not synthesize "Hotovost přijata".
  // R1/R2 passthrough: stale COMPLETED+!awaiting mismatch → shell null; awaiting/match keep
  // even if rail denied/unknown. Cold synthesize only when cashRailState === 'allowed'.
  const cashCollectShellResult = useMemo((): CashPaymentStartResponseDTO | null => {
    const resolvedSalesPointId =
      checkoutSession !== null
        ? resolveCheckoutSalesPointIdFromSession(checkoutSession)
        : null;
    return resolveCashCollectShellOwnership({
      cashCompleteResult,
      journeyPaymentId,
      checkoutSessionPaymentId: checkoutSession?.paymentId,
      checkoutSessionStatus: checkoutSession?.status,
      paymentStatusSettled,
      paymentStatus: journeyPaymentStatus.data?.status,
      awaitingCashConfirmation: journeyPaymentStatus.data?.awaitingCashConfirmation,
      cashRailState,
      cashColdResumePending,
      cashResumePollHint,
      sessionId: sessionIdFromUrl,
      salesPointId: resolvedSalesPointId,
      amountMinor: checkoutSession?.amountMinor ?? 0,
      currency: resolveDisplayCurrencyFromSession(checkoutSession?.currency),
      transactionId: checkoutSession?.transactionId ?? null,
      sessionSawAwaitingCash,
    }).shell;
  }, [
    cashColdResumePending,
    cashCompleteResult,
    cashRailState,
    cashResumePollHint,
    checkoutSession,
    journeyPaymentId,
    journeyPaymentStatus.data?.awaitingCashConfirmation,
    journeyPaymentStatus.data?.status,
    paymentStatusSettled,
    sessionIdFromUrl,
    sessionSawAwaitingCash,
  ]);
  const cashCollectOrderNumber = useMemo(() => {
    if (cashCollectShellResult === null) {
      return null;
    }
    return resolveCashCollectOrderNumber(cashCollectShellResult, journeyPaymentStatus.data);
  }, [cashCollectShellResult, journeyPaymentStatus.data]);
  // Prefer offered methods (post readiness + gateway probe), not raw surface count.
  const skipPaymentMethodPicker =
    !gatewayMethodsProbePending && visiblePaymentMethods.length === 1;

  const selectedPaymentMethod = useMemo((): CustomerPaymentMethodId | null => {
    if (visiblePaymentMethods.length === 0) {
      return null;
    }
    if (
      selectedPaymentMethodOverride !== null &&
      visiblePaymentMethods.includes(selectedPaymentMethodOverride)
    ) {
      return selectedPaymentMethodOverride;
    }
    return visiblePaymentMethods[0] ?? null;
  }, [selectedPaymentMethodOverride, visiblePaymentMethods]);

  const paymentPickerOptions = useMemo(
    () =>
      visiblePaymentMethods.map((id) => ({
        id,
        testId: resolvePaymentMethodTestId(id),
      })),
    [visiblePaymentMethods],
  );

  const showSlaNotice =
    !slaNoticeDismissed &&
    shouldShowPayOnSpotSlaNotice({
      checkoutMode: effectiveCheckoutMode,
      collect: sessionCollect,
      purposeType: checkoutSession?.purposeType ?? 'PRODUCT',
    });

  useEffect(() => {
    if (sessionIdFromUrl === null || sessionLoading || checkoutSession === null) {
      return;
    }
    if (activeQrPayment !== null || gateway !== null) {
      return;
    }
    if (gatewayMethodsProbePending) {
      return;
    }
    if (paymentMethodViewedSessionRef.current === sessionIdFromUrl) {
      return;
    }

    const availableMethods: string[] = [...visiblePaymentMethods];

    analytics.track({
      eventName: 'payment_method_viewed' as AnalyticsEventName,
      flow: paymentAnalyticsFlow,
      screenName: 'checkout_product',
      metadata: {
        available_methods: availableMethods,
        available_method_count: availableMethods.length,
        has_qr: mobileQrAllowed,
        has_card: mobileGatewayAllowed,
      },
    });
    paymentMethodViewedSessionRef.current = sessionIdFromUrl;
  }, [
    analytics,
    checkoutSession,
    gateway,
    gatewayMethodsProbePending,
    mobileGatewayAllowed,
    mobileQrAllowed,
    paymentAnalyticsFlow,
    activeQrPayment,
    sessionIdFromUrl,
    sessionLoading,
    visiblePaymentMethods,
  ]);

  // Paid/cancelled checkout URL with no live QR/gateway UI → shop (new cart), not another pay start.
  // Stale gateway must not block forever: recoverFromTerminal clears qr/gateway and writes shop flash.
  // In-flight gateway redirect only applies while status is non-terminal (window.location.assign).
  // G1: recover when journey payment is COMPLETED while session cache still AWAITING_PAYMENT —
  // but never while live QR/cash shell is mounted (guest must save QR via PostPayCta / continue).
  useEffect(() => {
    if (sessionLoading || checkoutSession === null || sessionIdFromUrl === null) {
      return;
    }
    const sessionTerminal = isTerminalCheckoutSessionStatus(checkoutSession.status);
    const paymentUiCompleted = paymentStatusCompleted;
    if (!sessionTerminal && !paymentUiCompleted) {
      return;
    }
    // Only a real cash-collect shell owns the flow until continue (G1).
    // cashResumePollHint alone (bank-transfer lag / FIO COMPLETED) must not block shop recovery.
    if (cashCollectShellResult !== null && !sessionTerminal) {
      return;
    }
    if (activeQrPayment !== null && (qr !== null || pendingQr !== null)) {
      return;
    }
    // Defer setState — react-hooks/set-state-in-effect forbids sync setState in effect body.
    queueMicrotask(() => {
      recoverFromTerminalCheckoutSession(sessionIdFromUrl);
    });
  }, [
    activeQrPayment,
    cashCollectShellResult,
    checkoutSession,
    paymentStatusCompleted,
    pendingQr,
    qr,
    recoverFromTerminalCheckoutSession,
    sessionIdFromUrl,
    sessionLoading,
  ]);

  useEffect(() => {
    if (sessionIdFromUrl === null || sessionLoading || checkoutSession === null) {
      return;
    }
    if (boundSessionIdForStepRef.current === sessionIdFromUrl) {
      return;
    }
    boundSessionIdForStepRef.current = sessionIdFromUrl;
    const persisted = readCheckoutInFlowStep(sessionIdFromUrl);
    const restored = resolveRestoredProductCheckoutStep({
      persistedInFlowStep: persisted,
      purposeType: checkoutSession.purposeType,
      collect: checkoutSession.collect,
      sessionStatus: checkoutSession.status,
      falseCollectForce: readCheckoutFalseCollectForce(sessionIdFromUrl),
      fulfillmentMode: checkoutSession.fulfillmentMode,
    });
    applyRestoredCheckoutStep(sessionIdFromUrl, restored);
    setCollectSelection(checkoutSession.collect ?? null);
    setSelfCollectConfirmed(checkoutSession.collect?.selfCollectConfirmedAtKiosk === true);
    // G10 — remount awaiting cash-collect shell after SPA POP / restoreLeft (MOB-05).
    // R2/G2 — only when cashRailState === 'denied' + COMPLETED + !awaiting; never on 'unknown'.
    // R1 owns paymentId-mismatch clear when CASH still allowed; remount skips matching paid (R2).
    const remountSid = sessionIdFromUrl;
    const remountCashRailState = cashRailState;
    const remountPaymentStatus = journeyPaymentStatus.data?.status;
    const remountAwaitingCash = journeyPaymentStatus.data?.awaitingCashConfirmation;
    const remountJourneyPay =
      (journeyPaymentId?.trim() ?? '') || (checkoutSession.paymentId?.trim() ?? '');
    queueMicrotask(() => {
      setCashCompleteResult((current) => {
        if (current !== null) {
          // Rebind guard: never keep React cash state for a foreign session.
          if (current.checkoutSessionId !== remountSid) {
            clearCheckoutCashCollectResult(remountSid);
            return null;
          }
          return current;
        }
        const persisted = readCheckoutCashCollectResult(remountSid);
        if (persisted === null) {
          return null;
        }
        if (
          remountCashRailState === 'denied' &&
          remountPaymentStatus === 'COMPLETED' &&
          remountAwaitingCash !== true
        ) {
          const dtoPay = persisted.paymentId?.trim() ?? '';
          // R2 keep-until-continue: matching paid cash holds even when rail denied.
          if (
            dtoPay.length > 0 &&
            remountJourneyPay.length > 0 &&
            dtoPay === remountJourneyPay
          ) {
            return persisted;
          }
          clearCheckoutCashCollectResult(remountSid);
          return null;
        }
        return persisted;
      });
    });
  }, [
    cashRailState,
    checkoutSession,
    journeyPaymentId,
    journeyPaymentStatus.data?.awaitingCashConfirmation,
    journeyPaymentStatus.data?.status,
    sessionIdFromUrl,
    sessionLoading,
  ]);

  // G2/R2 — settle wipe only when cashRailState === 'denied' (never 'unknown').
  // True cash awaiting (awaiting===true) kept; matching paid kept until continue (R2).
  useEffect(() => {
    if (sessionIdFromUrl === null) {
      return;
    }
    if (cashRailState !== 'denied') {
      return;
    }
    if (journeyPaymentStatus.data?.status !== 'COMPLETED') {
      return;
    }
    if (journeyPaymentStatus.data?.awaitingCashConfirmation === true) {
      return;
    }
    const persisted = readCheckoutCashCollectResult(sessionIdFromUrl);
    if (persisted === null && cashCompleteResult === null) {
      return;
    }
    // R2 keep-until-continue when paymentIds match (even if rail denied).
    if (cashCompleteResult !== null && cashDtoPaymentIdsMatchJourney) {
      return;
    }
    if (persisted !== null) {
      const dtoPay = persisted.paymentId?.trim() ?? '';
      const journeyPay =
        (journeyPaymentId?.trim() ?? '') ||
        (checkoutSession?.paymentId?.trim() ?? '');
      if (dtoPay.length > 0 && journeyPay.length > 0 && dtoPay === journeyPay) {
        return;
      }
    }
    const sid = sessionIdFromUrl;
    queueMicrotask(() => {
      clearCheckoutCashCollectResult(sid);
      setCashCompleteResult(null);
    });
  }, [
    cashCompleteResult,
    cashDtoPaymentIdsMatchJourney,
    cashRailState,
    checkoutSession?.paymentId,
    journeyPaymentId,
    journeyPaymentStatus.data?.awaitingCashConfirmation,
    journeyPaymentStatus.data?.status,
    sessionIdFromUrl,
  ]);

  // R1/G-INIT — settled COMPLETED + !awaiting + paymentId mismatch/empty: drop stale DTO.
  // KEEP matching paid cash (T6) via shouldClearStaleCashCollectResult === false.
  useEffect(() => {
    if (sessionIdFromUrl === null) {
      return;
    }
    if (
      !shouldClearStaleCashCollectResult({
        cashCompleteResult,
        journeyPaymentId,
        checkoutSessionPaymentId: checkoutSession?.paymentId,
        paymentStatusSettled,
        paymentStatus: journeyPaymentStatus.data?.status,
        awaitingCashConfirmation: journeyPaymentStatus.data?.awaitingCashConfirmation,
        sessionSawAwaitingCash,
      })
    ) {
      return;
    }
    const sid = sessionIdFromUrl;
    queueMicrotask(() => {
      clearCheckoutCashCollectResult(sid);
      setCashCompleteResult(null);
    });
  }, [
    cashCompleteResult,
    checkoutSession?.paymentId,
    journeyPaymentId,
    journeyPaymentStatus.data?.awaitingCashConfirmation,
    journeyPaymentStatus.data?.status,
    paymentStatusSettled,
    sessionIdFromUrl,
    sessionSawAwaitingCash,
  ]);

  // Stage 2 §18 — cold resume cash waiting shell from backend when sessionStorage empty.
  // Only when staff-cash awaiting is authoritative — never hydrate on bare COMPLETED
  // (FIO / bank-transfer settle must not become "Hotovost přijata").
  // R5 — hydrate only when cashRailState === 'allowed' (unknown acts as not allowed).
  useEffect(() => {
    if (sessionIdFromUrl === null || sessionLoading || checkoutSession === null) {
      return;
    }
    if (cashCompleteResult !== null) {
      return;
    }
    if (cashRailState !== 'allowed') {
      return;
    }
    if (checkoutSession.status !== 'AWAITING_PAYMENT') {
      return;
    }
    const paymentId = checkoutSession.paymentId?.trim() ?? '';
    if (paymentId.length === 0) {
      return;
    }
    if (journeyPaymentStatus.data?.awaitingCashConfirmation !== true) {
      return;
    }
    const salesPointId = resolveCheckoutSalesPointIdFromSession(checkoutSession);
    if (salesPointId === null || salesPointId <= 0) {
      return;
    }
    const hydrated: CashPaymentStartResponseDTO = {
      checkoutSessionId: sessionIdFromUrl,
      salesPointId,
      amountMinor: checkoutSession.amountMinor,
      currency: checkoutSession.currency,
      paymentId,
      transactionId: checkoutSession.transactionId ?? undefined,
      awaitingCashConfirmation: true,
    };
    queueMicrotask(() => {
      setCashCompleteResult(hydrated);
      writeCheckoutCashCollectResult(sessionIdFromUrl, hydrated);
    });
  }, [
    cashCompleteResult,
    cashRailState,
    checkoutSession,
    journeyPaymentStatus.data?.awaitingCashConfirmation,
    sessionIdFromUrl,
    sessionLoading,
  ]);

  // G3 — thin re-eval: after first bind, usable collect + false-collect force → payment.
  // Intentional Back clears the force flag (MOB-05); do not re-promote then.
  // Defer setState — react-hooks/set-state-in-effect forbids sync setState in effect body.
  useEffect(() => {
    if (sessionIdFromUrl === null || sessionLoading || checkoutSession === null) {
      return;
    }
    if (boundSessionIdForStepRef.current !== sessionIdFromUrl) {
      return;
    }
    if (
      !shouldPromoteFalseCollectRecovery({
        falseCollectForce: readCheckoutFalseCollectForce(sessionIdFromUrl),
        purposeType: checkoutSession.purposeType,
        collect: checkoutSession.collect,
        currentStep: checkoutStep,
      })
    ) {
      return;
    }
    const sid = sessionIdFromUrl;
    scheduleFalseCollectRecoveryPromote({
      sid,
      purposeType: checkoutSession.purposeType,
      readCollect: () => collectRef.current,
      readFalseCollectForce: readCheckoutFalseCollectForce,
      readCurrentStep: () => checkoutStepRef.current,
      writeInFlowPayment: (sessionId) => writeCheckoutInFlowStep(sessionId, 'payment'),
      clearFalseCollectForce: clearCheckoutFalseCollectForce,
      onPromote: ({ nextCollect: promotedCollect, nextSelfCollect: promotedSelfCollect }) => {
        setCollectSelection(promotedCollect);
        setSelfCollectConfirmed(promotedSelfCollect);
        setCheckoutStep('payment');
      },
    });
  }, [checkoutSession, checkoutStep, sessionIdFromUrl, sessionLoading]);

  useEffect(() => {
    if (checkoutStep !== 'payment' || salesPointId === null || salesPointId <= 0) {
      return;
    }
    void refetchKioskCapabilities();
  }, [checkoutStep, refetchKioskCapabilities, salesPointId]);

  useEffect(() => {
    if (!salesPointDrift || sessionIdFromUrl === null) {
      return;
    }
    void invalidateCheckoutSession(sessionIdFromUrl);
  }, [salesPointDrift, sessionIdFromUrl, invalidateCheckoutSession]);

  const refreshSalesPointCapabilitiesIfStale = async (): Promise<boolean> => {
    if (salesPointId === null || salesPointId <= 0) {
      return true;
    }
    const stale = isSalesPointCapabilityStale({
      fetchedAtMs: kioskInfo.dataUpdatedAt,
      nowMs: Date.now(),
    });
    if (!stale || capabilityRefreshInFlightRef.current) {
      return true;
    }
    capabilityRefreshInFlightRef.current = true;
    try {
      await kioskInfo.refetch();
      return true;
    } catch (err) {
      showPaymentError(
        toCustomerFacingErrorMessage(err, {
          fallback: t('checkout.sessionLoadError'),
        }),
        'refreshSalesPointCapabilities',
        err,
      );
      return false;
    } finally {
      capabilityRefreshInFlightRef.current = false;
    }
  };

  const persistCollectBeforePay = async (
    sid: string,
    activeSession: CheckoutSessionDTO | null = checkoutSession,
  ): Promise<boolean> => {
    try {
      return await runPersistCollectBeforePay({
        sid,
        activeSession,
        collectPayloadForPersist,
        selectedCheckoutMode,
        effectivePickupSlot,
        sessionIdFromUrl,
        writeCheckoutInFlowStep,
        setCheckoutStep,
        markCheckoutFalseCollectForce,
        clearCheckoutFalseCollectForce,
        showPaymentError,
        collectMetadataRequiredMessage: t('errors.collectMetadataRequired'),
        updateCollectMutateAsync: (args) => updateCollect.mutateAsync(args),
        buildCheckoutMode: (mode) =>
          buildCheckoutModeForCollectPatch(
            mode,
            kioskInfo.data?.commerceConfigJson ?? null,
            effectiveHandoffMode,
          ),
        rememberCheckoutModeIfNeeded: (session, mode) => {
          if (session.kioskId !== null && session.kioskId > 0) {
            rememberCheckoutMode(tenantCode, session.kioskId, mode);
          }
        },
      });
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_MODE_REQUIRED_RECREATE_SESSION'
      ) {
        showPaymentError(donationErrorMessage(err.code), 'checkoutModeRecreate');
        if (sessionIdFromUrl !== null) {
          clearCheckoutInFlowStep(sessionIdFromUrl);
        }
        navigate(buildShopRecoveryPath(), { replace: true });
        return false;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        await invalidateCheckoutSession(sid);
        recoverFromTerminalCheckoutSession(sid);
        return false;
      }
      if (
        err instanceof ApiError &&
        err.code === 'COLLECT_LOCKED_AFTER_PAYMENT_START'
      ) {
        return true;
      }
      if (
        tryBounceOnCollectMetadataRequired(
          err,
          bounceToCollectForMetadataRequired,
          'collectMetadataRequiredPatch',
        )
      ) {
        return false;
      }
      throw err;
    }
  };

  const resolveEmailOrFail = (): string | null => {
    const candidate = email.trim();
    if (candidate.length === 0) {
      setEmailError(null);
      return '';
    }
    if (!SIMPLE_EMAIL_REGEX.test(candidate)) {
      setEmailError(t('checkout.invalidEmail'));
      return null;
    }
    setEmailError(null);
    return candidate;
  };

  const ensureSessionId = (): string | null => {
    if (sessionIdFromUrl !== null && sessionIdFromUrl.length > 0) {
      return sessionIdFromUrl;
    }
    return null;
  };

  const handleCollectContinue = async (): Promise<void> => {
    if (collectContinueBlocked || collectPayloadForPersist === null || sessionIdFromUrl === null) {
      if (selfCollectOnlyGate && !selfCollectConfirmed) {
        analytics.track({
          eventName: 'pickup_gate_blocked' as AnalyticsEventName,
          flow: paymentAnalyticsFlow,
          screenName: 'checkout_product_collect',
          metadata: {},
        });
      }
      return;
    }
    const mode = selectedCheckoutMode;
    const checkoutMode = buildCheckoutModeForCollectPatch(
      mode,
      kioskInfo.data?.commerceConfigJson ?? null,
      effectiveHandoffMode,
    );
    try {
      await updateCollect.mutateAsync({
        sessionId: sessionIdFromUrl,
        collect: collectPayloadForPersist,
        checkoutMode,
      });
      setCollectSelection(collectPayloadForPersist);
      setCheckoutStepPersisted('payment');
      pushCheckoutInFlowHistory();
      setPaymentError(null);
      if (checkoutSession?.kioskId !== null && checkoutSession?.kioskId !== undefined && checkoutSession.kioskId > 0) {
        rememberCheckoutMode(tenantCode, checkoutSession.kioskId, mode);
      }
      await invalidateCheckoutSession(sessionIdFromUrl);
      await kioskInfo.refetch();
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_MODE_REQUIRED_RECREATE_SESSION'
      ) {
        showPaymentError(donationErrorMessage(err.code), 'checkoutModeRecreate');
        if (sessionIdFromUrl !== null) {
          clearCheckoutInFlowStep(sessionIdFromUrl);
        }
        navigate(buildShopRecoveryPath(), { replace: true });
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        const sid = sessionIdFromUrl;
        if (sid !== null) {
          await invalidateCheckoutSession(sid);
          recoverFromTerminalCheckoutSession(sid);
        }
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'COLLECT_LOCKED_AFTER_PAYMENT_START'
      ) {
        setCheckoutStepPersisted('payment');
        const lockedSid = sessionIdFromUrl;
        if (lockedSid !== null) {
          await refetchCheckoutSessionIntoRef();
          if (await resumeQrFromSession(lockedSid)) {
            return;
          }
        } else {
          showPaymentError(t('checkout.modeLockedPaymentInProgress'), 'collectLockedAfterPayment');
        }
        return;
      }
      const message =
        err instanceof ApiError
          ? donationErrorMessage(err.code, t('checkout.sessionLoadError'))
          : toCustomerFacingErrorMessage(err, {
              fallback: t('checkout.sessionLoadError'),
            });
      showPaymentError(message, 'updateCollectBeforePayment', err);
    }
  };

  /**
   * G4 — phone-first SHIPPING continue mirrors ShopCheckoutOverlays → handleShippingContinue:
   * recreate session with fulfillmentMode=SHIPPING + same shipping payload (charge 0), then
   * replace-nav (initial step resolves to payment; collect omitted).
   */
  const handleShippingContinue = async (
    shipping: ShippingCheckoutPayload,
  ): Promise<void> => {
    if (
      checkoutSession === null ||
      salesPointId === null ||
      shippingContinuePending ||
      createCheckoutSessionForCurrency.isPending ||
      selfScanCheckoutHandoff ||
      sellerAssistedCheckout
    ) {
      return;
    }
    if (checkoutSession.lines.length === 0) {
      return;
    }
    setShippingContinuePending(true);
    setPaymentError(null);
    try {
      const mode = selectedCheckoutMode;
      const checkoutMode = buildCheckoutModeForCollectPatch(
        mode,
        kioskInfo.data?.commerceConfigJson ?? null,
        effectiveHandoffMode,
      );
      const lines = checkoutSession.lines.map((line) => ({
        productId: line.productId,
        variantId: line.variantId,
        quantity: line.quantity,
      }));
      const currency = resolveDisplayCurrencyFromSession(checkoutSession.currency);
      const session = await createCheckoutSessionForCurrency.mutateAsync({
        currency,
        purposeType: checkoutSession.purposeType,
        flowType: checkoutSession.flowType,
        entryChannel: checkoutSession.entryChannel,
        kioskId: salesPointId,
        checkoutSubMode: mode,
        checkoutMode,
        fulfillmentMode: 'SHIPPING',
        shipping,
        lines,
        donationProjectCode: checkoutSession.donationProjectCode,
        idempotencyKey: `ship:pf:${tenantCode}:${salesPointId}:${sessionIdFromUrl ?? 'x'}:${Date.now()}`,
      });
      if (sessionIdFromUrl !== null) {
        clearCheckoutInFlowStep(sessionIdFromUrl);
      }
      rememberCheckoutMode(tenantCode, salesPointId, mode);
      navigate(
        buildCheckoutNavigatePath(tenantCode, session.id, {
          preserveFromSearch: location.search,
        }),
        { replace: true },
      );
    } catch (err) {
      const message =
        err instanceof ApiError
          ? donationErrorMessage(err.code, t('checkout.sessionLoadError'))
          : toCustomerFacingErrorMessage(err, {
              fallback: t('checkout.sessionLoadError'),
            });
      showPaymentError(message, 'createShippingCheckoutSession', err);
    } finally {
      setShippingContinuePending(false);
    }
  };

  const handleQr = async (): Promise<void> => {
    setPaymentError(null);
    const sid = ensureSessionId();
    if (sid === null || checkoutSession === null) {
      return;
    }
    if (sellerAssistedStaleHandoff) {
      showPaymentError(t('checkout.sellerAssisted.staleHandoffTitle'), 'sellerAssistedStale');
      return;
    }
    prepareSellerAssistedJwtRebindAtPaymentStart({
      session: checkoutSession,
      authenticatedCustomerId: authSession?.customerId ?? null,
    });
    if (isTerminalCheckoutSessionStatus(checkoutSession.status)) {
      recoverFromTerminalCheckoutSession(sid);
      return;
    }
    const capabilitiesReady = await refreshSalesPointCapabilitiesIfStale();
    if (!capabilitiesReady) {
      return;
    }
    const resolvedEmail = resolveEmailOrFail();
    if (resolvedEmail === null) {
      return;
    }
    analytics.track({
      eventName: 'payment_started',
      flow: paymentAnalyticsFlow,
      screenName: 'checkout_product',
      metadata: { paymentMethod: 'QR' },
    });
    try {
      const freshSessionResult = await sessionQuery.refetch();
      const activeSession = freshSessionResult.data ?? checkoutSession;
      if (isTerminalCheckoutSessionStatus(activeSession.status)) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      // Unpaid reuse: always POST start-qr so backing out of payment reloads
      // SPAYD / qrVisible from the server. Client stub resume hid the QR.
      const collectOk = await persistCollectBeforePay(sid, activeSession);
      if (!collectOk) {
        return;
      }
      const paymentIncentives = resolveCheckoutPaymentIncentives({
        tenantCode,
        checkoutSessionId: sid,
        serverActivatedPromoRewardId:
          activeSession?.promotions?.activatedPromoRewardId ?? null,
        serverPromoResolved: activeSession != null,
      });
      // Exclusive promo-only / sticky clear → null (do not ??-revive handoff).
      const activatedCouponId = promoExclusiveConflict.effectiveActivatedCouponId;
      // Server null (RELEASE) wins; exclusive null (choose loyalty / cleared handoff) blocks stale server id.
      const activatedPromoRewardId =
        paymentIncentives.activatedPromoRewardId == null
          ? null
          : promoExclusiveConflict.effectiveActivatedPromoRewardId;
      const res = await startQr.mutateAsync({
        sessionId: sid,
        analyticsSessionId: sid,
        ...(resolvedEmail.length > 0 ? { customerEmail: resolvedEmail } : {}),
        // Present null clears BE session fallback (loyalty-only / promo-only / RELEASE).
        activatedCouponId,
        activatedPromoRewardId,
      });
      if (pricingPreview.data?.promo != null) {
        savePromoReceiptSnapshot(tenantCode, res.paymentId, {
          adjustments: pricingPreview.data.promo.adjustments.map((adjustment) => ({
            ...adjustment,
            eventName: promoEventNameById.get(adjustment.eventId),
          })),
          cartDiscountAmount: pricingPreview.data.promo.cartDiscountAmount,
        });
      }
      if (!(await mountLiveStartQrOrDeny(sid, res))) {
        return;
      }
      pushCheckoutInFlowHistory();
      paymentSubmittedRef.current = true;
      emitPaymentSubmitted(trackRef.current, 'checkout_product', 'QR', paymentAnalyticsFlow);
      await invalidateCheckoutSession(sid);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TENANT_SWITCH_REDIRECT') {
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'COLLECT_LOCKED_AFTER_PAYMENT_START'
      ) {
        await refetchCheckoutSessionIntoRef();
        const lockedSid = sessionIdFromUrl;
        if (lockedSid !== null && (await resumeQrFromSession(lockedSid))) {
          return;
        }
        if (lockedSid === null) {
          showPaymentError(t('checkout.modeLockedPaymentInProgress'), 'collectLockedAfterPayment');
        }
        return;
      }
      if (tryBounceOnCollectMetadataRequired(err, bounceToCollectForMetadataRequired, 'startQrCollectRequired')) {
        return;
      }
      const message = resolveCustomerPaymentStartErrorMessage(
        err,
        t('checkout.paymentStartFailed'),
        (key) => t(key),
      );
      showPaymentError(message, 'startQrPayment', err);
    }
  };

  const handleGatewayStart = async (input?: {
    readonly gatewayProviderId?: string;
  }): Promise<void> => {
    setPaymentError(null);
    const sid = ensureSessionId();
    if (sid === null || checkoutSession === null) {
      return;
    }
    if (sellerAssistedStaleHandoff) {
      showPaymentError(t('checkout.sellerAssisted.staleHandoffTitle'), 'sellerAssistedStale');
      return;
    }
    // FR-06 / §3.5 — association-only JWT rebind happens in StartCustomerCheckoutGatewayPayment
    // when optionalCustomerAuth supplies authenticatedCustomerId. ApiClient already sends
    // credentials/Bearer when signed in; do not bind in handoff resolve.
    prepareSellerAssistedJwtRebindAtPaymentStart({
      session: checkoutSession,
      authenticatedCustomerId: authSession?.customerId ?? null,
    });
    if (isTerminalCheckoutSessionStatus(checkoutSession.status)) {
      recoverFromTerminalCheckoutSession(sid);
      return;
    }
    const capabilitiesReady = await refreshSalesPointCapabilitiesIfStale();
    if (!capabilitiesReady) {
      return;
    }
    const resolvedEmail = resolveEmailOrFail();
    if (resolvedEmail === null) {
      return;
    }
    analytics.track({
      eventName: 'payment_started',
      flow: paymentAnalyticsFlow,
      screenName: 'checkout_product',
      metadata: { paymentMethod: 'CARD' },
    });
    try {
      // Parity with QR: refetch so RELEASE (server null) wins over stale cached reward.
      const freshSessionResult = await sessionQuery.refetch();
      const activeSession = freshSessionResult.data ?? checkoutSession;
      if (isTerminalCheckoutSessionStatus(activeSession.status)) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      const collectOk = await persistCollectBeforePay(sid, activeSession);
      if (!collectOk) {
        return;
      }
      const paymentIncentives = resolveCheckoutPaymentIncentives({
        tenantCode,
        checkoutSessionId: sid,
        serverActivatedPromoRewardId:
          activeSession?.promotions?.activatedPromoRewardId ?? null,
        serverPromoResolved: activeSession != null,
      });
      // Exclusive promo-only / sticky clear → null (do not ??-revive handoff).
      const activatedCouponId = promoExclusiveConflict.effectiveActivatedCouponId;
      // Server null (RELEASE) wins; exclusive null (choose loyalty / cleared handoff) blocks stale server id.
      const activatedPromoRewardId =
        paymentIncentives.activatedPromoRewardId == null
          ? null
          : promoExclusiveConflict.effectiveActivatedPromoRewardId;
      const res = await startGateway.mutateAsync({
        sessionId: sid,
        analyticsSessionId: sid,
        ...(resolvedEmail.length > 0 ? { customerEmail: resolvedEmail } : {}),
        ...(input?.gatewayProviderId !== undefined
          ? { gatewayProviderId: input.gatewayProviderId }
          : {}),
        // Present null clears BE session fallback (loyalty-only / promo-only / RELEASE).
        activatedCouponId,
        activatedPromoRewardId,
      });
      if (pricingPreview.data?.promo != null) {
        savePromoReceiptSnapshot(tenantCode, res.paymentId, {
          adjustments: pricingPreview.data.promo.adjustments.map((adjustment) => ({
            ...adjustment,
            eventName: promoEventNameById.get(adjustment.eventId),
          })),
          cartDiscountAmount: pricingPreview.data.promo.cartDiscountAmount,
        });
      }
      // G4 — leaving cash for gateway (handleOtherPayment / method select → handleGatewayStart).
      clearCheckoutCashCollectResult(sid);
      setCashCompleteResult(null);
      setGateway(res);
      await invalidateCheckoutSession(sid);
      paymentSubmittedRef.current = true;
      emitPaymentSubmitted(trackRef.current, 'checkout_product', 'CARD', paymentAnalyticsFlow);
      if (/^https?:\/\//i.test(res.redirectUrl)) {
        window.location.assign(res.redirectUrl);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TENANT_SWITCH_REDIRECT') {
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      if (tryBounceOnCollectMetadataRequired(err, bounceToCollectForMetadataRequired, 'startGatewayCollectRequired')) {
        return;
      }
      const message = resolveCustomerPaymentStartErrorMessage(
        err,
        t('checkout.paymentStartFailed'),
        (key) => t(key),
      );
      showPaymentError(message, 'startGatewayPayment', err);
    } finally {
      setGatewayStartingMethodId(null);
    }
  };

  const handleCash = async (): Promise<void> => {
    setPaymentError(null);
    const sid = ensureSessionId();
    if (sid === null || checkoutSession === null) {
      return;
    }
    if (sellerAssistedStaleHandoff) {
      showPaymentError(t('checkout.sellerAssisted.staleHandoffTitle'), 'sellerAssistedStale');
      return;
    }
    prepareSellerAssistedJwtRebindAtPaymentStart({
      session: checkoutSession,
      authenticatedCustomerId: authSession?.customerId ?? null,
    });
    if (isTerminalCheckoutSessionStatus(checkoutSession.status)) {
      recoverFromTerminalCheckoutSession(sid);
      return;
    }
    const capabilitiesReady = await refreshSalesPointCapabilitiesIfStale();
    if (!capabilitiesReady) {
      return;
    }
    analytics.track({
      eventName: 'payment_started',
      flow: paymentAnalyticsFlow,
      screenName: 'checkout_product',
      metadata: { paymentMethod: 'CASH' },
    });
    try {
      const freshSessionResult = await sessionQuery.refetch();
      const activeSession = freshSessionResult.data ?? checkoutSession;
      if (isTerminalCheckoutSessionStatus(activeSession.status)) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      const collectOk = await persistCollectBeforePay(sid, activeSession);
      if (!collectOk) {
        return;
      }
      const paymentIncentives = resolveCheckoutPaymentIncentives({
        tenantCode,
        checkoutSessionId: sid,
        serverActivatedPromoRewardId:
          activeSession?.promotions?.activatedPromoRewardId ?? null,
        serverPromoResolved: activeSession != null,
      });
      const activatedCouponId = promoExclusiveConflict.effectiveActivatedCouponId;
      const activatedPromoRewardId =
        paymentIncentives.activatedPromoRewardId == null
          ? null
          : promoExclusiveConflict.effectiveActivatedPromoRewardId;
      const startRes = await startCash.mutateAsync({
        sessionId: sid,
        analyticsSessionId: sid,
        activatedCouponId,
        activatedPromoRewardId,
      });
      setCashCompleteResult(startRes);
      writeCheckoutCashCollectResult(sid, startRes);
      paymentSubmittedRef.current = true;
      // Stage 1 cash (aligned with kiosk): cash intent is declared at startCash request;
      // financial completion happens only after staff confirms via pickup queue. Emit
      // payment_submitted here — not on staff confirm — so analytics records intent.
      emitPaymentSubmitted(trackRef.current, 'checkout_product', 'CASH', paymentAnalyticsFlow);
      await invalidateCheckoutSession(sid);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TENANT_SWITCH_REDIRECT') {
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        recoverFromTerminalCheckoutSession(sid);
        return;
      }
      if (
        err instanceof ApiError &&
        err.code === 'COLLECT_LOCKED_AFTER_PAYMENT_START'
      ) {
        showPaymentError(t('checkout.modeLockedPaymentInProgress'), 'collectLockedAfterPayment');
        return;
      }
      if (tryBounceOnCollectMetadataRequired(err, bounceToCollectForMetadataRequired, 'startCashCollectRequired')) {
        return;
      }
      const message = resolveCustomerPaymentStartErrorMessage(
        err,
        t('checkout.paymentStartFailed'),
        (key) => t(key),
      );
      showPaymentError(message, 'startCashPayment', err);
    }
  };

  const handleOtherPayment = async (): Promise<void> => {
    setPaymentError(null);
    if (salesPointId === null || salesPointId <= 0) {
      showPaymentError(t('payment.cardUnavailable'), 'gatewayMethodsMissingSalesPoint');
      return;
    }
    const sid = ensureSessionId();
    if (sid !== null && checkoutSession !== null && isTerminalCheckoutSessionStatus(checkoutSession.status)) {
      recoverFromTerminalCheckoutSession(sid);
      return;
    }
    const resolvedEmail = resolveEmailOrFail();
    if (resolvedEmail === null) {
      return;
    }

    const probed = gatewayMethodsProbeQuery.data;
    const useCachedProbe =
      gatewayMethodsPayable === true && isGatewayMethodsPayable(probed);

    setGatewayMethodsLoading(true);
    try {
      if (gatewayMethodsProbeCurrency === null) {
        showPaymentError(t('payment.cardUnavailable'), 'gatewayMethodsMissingCurrency');
        return;
      }
      const gatewayResult = useCachedProbe
        ? probed
        : await fetchGatewayMethods({
            apiBaseUrl,
            tenantCode,
            salesPointId,
            currency: gatewayMethodsProbeCurrency,
            purpose: 'PRODUCT_PURCHASE',
          });
      if (gatewayResult === undefined || !isGatewayMethodsPayable(gatewayResult)) {
        showPaymentError(t('payment.cardUnavailable'), 'gatewayMethodsEmpty');
        return;
      }
      setGatewayMethodsResponse(gatewayResult);
      const methods = gatewayResult.methods;
      const slotId = resolveGatewayProviderSlotId(gatewayResult);
      if (methods.length === 1) {
        setGatewayStartingMethodId(methods[0]?.id ?? null);
        await handleGatewayStart(
          slotId !== undefined ? { gatewayProviderId: slotId } : undefined,
        );
        return;
      }
      setGatewayMethodOptions(methods);
      setPaymentRailUiStep('gateway-methods');
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.code === 'CHECKOUT_SESSION_INVALID_STATE'
      ) {
        const recoverySid = sessionIdFromUrl;
        if (recoverySid !== null) {
          await invalidateCheckoutSession(recoverySid);
          recoverFromTerminalCheckoutSession(recoverySid);
        }
        return;
      }
      const message = resolveCustomerPaymentStartErrorMessage(
        err,
        t('payment.cardUnavailable'),
        (key) => t(key),
      );
      showPaymentError(message, 'loadGatewayMethods', err);
    } finally {
      setGatewayMethodsLoading(false);
    }
  };

  const handleGatewayMethodSelect = (methodId: string): void => {
    setGatewayStartingMethodId(methodId);
    const slotId = resolveGatewayProviderSlotId(gatewayMethodsResponse);
    void handleGatewayStart(
      slotId !== undefined ? { gatewayProviderId: slotId } : undefined,
    );
  };

  const resolvePaymentConfirmLabel = (): string => {
    if (startQr.isPending) {
      return t('checkout.preparingQr');
    }
    if (gatewayMethodsLoading) {
      return t('payment.gatewayMethods.loading');
    }
    if (startGateway.isPending || gatewayStartingMethodId !== null) {
      return t('checkout.redirecting');
    }
    if (startCash.isPending) {
      return t('checkout.cashCollect.processing');
    }
    if (selectedPaymentMethod === 'QR') {
      return t('payment.method.qr');
    }
    if (selectedPaymentMethod === 'CARD') {
      return t('payment.method.cardPay');
    }
    if (selectedPaymentMethod === 'CASH') {
      return t('payment.method.cash');
    }
    return t('payment.payCta');
  };

  const handlePaymentConfirm = (): void => {
    if (selectedPaymentMethod === 'QR') {
      void handleQr();
      return;
    }
    if (selectedPaymentMethod === 'CASH') {
      void handleCash();
      return;
    }
    if (selectedPaymentMethod === 'CARD' || selectedPaymentMethod === 'WALLET') {
      void handleOtherPayment();
    }
  };

  const paymentConfirmPending =
    startQr.isPending ||
    startCash.isPending ||
    startGateway.isPending ||
    gatewayMethodsLoading ||
    gatewayStartingMethodId !== null;

  const continueAfterPay = (): void => {
    if (sessionIdFromUrl !== null) {
      clearCheckoutCashCollectResult(sessionIdFromUrl);
    }
    setCashCompleteResult(null);
    if (sessionIdFromUrl !== null) {
      clearCheckoutInFlowStep(sessionIdFromUrl);
      // Drop stale session so browser back cannot revive a paid checkout UI.
      void invalidateCheckoutSession(sessionIdFromUrl);
    }
    const navigateReplace = (to: string): void => {
      navigate(to, { replace: true });
    };
    const kioskId = checkoutSession?.kioskId ?? null;
    if (kioskId !== null && kioskId > 0) {
      const rememberedSlug = readLastKnownSalesPointSlug(tenantCode);
      if (rememberedSlug !== null) {
        navigateReplace(`/${encodeURIComponent(tenantCode)}/${encodeURIComponent(rememberedSlug)}`);
        return;
      }
      const slugFromKiosk = kioskInfo.data?.code?.trim();
      if (slugFromKiosk && slugFromKiosk.length > 0) {
        navigateReplace(`/${encodeURIComponent(tenantCode)}/${encodeURIComponent(slugFromKiosk)}`);
        return;
      }
      navigateReplace(
        `/${encodeURIComponent(tenantCode)}/shop?kioskId=${encodeURIComponent(String(kioskId))}`,
      );
      return;
    }
    navigateReplace(`/${encodeURIComponent(tenantCode)}/shops`);
  };

  let activeStep: PhoneFirstCheckoutActiveStep;
  if (sessionIdFromUrl === null) {
    activeStep = 'missing-session';
  } else if (cashCollectShellResult !== null) {
    activeStep = 'cash-collect';
  } else if (activeQrPayment !== null) {
    activeStep = 'qr';
  } else if (
    !isDonationSession &&
    activeCheckoutStep === 'collect' &&
    activeQrPayment === null &&
    gateway === null
  ) {
    activeStep = 'collect';
  } else {
    activeStep = 'payment';
  }

  // G2 backup: cart / Organization unmount without popCheckoutStepBack.
  // G1 chrome click persist is primary (sync before navigate). This unmount
  // microtask can epoch-skip on same-turn remount. Epoch still skips StrictMode
  // + in-place step change so handleQr's in-flow `qr` write is not undone.
  useEffect(() => {
    checkoutLeaveOffQrEpoch += 1;
    const epoch = checkoutLeaveOffQrEpoch;
    const sid = sessionIdFromUrl;
    const qrStepActive = activeStep === 'qr';
    return () => {
      queueMicrotask(() => {
        if (epoch !== checkoutLeaveOffQrEpoch) {
          return;
        }
        if (sid === null) {
          return;
        }
        persistCheckoutLeaveOffQrStep(sid, { qrStepActive });
      });
    };
  }, [sessionIdFromUrl, activeStep]);

  const locationPathAndSearch = `${location.pathname}${location.search}`;

  const pushCheckoutInFlowHistory = useCallback((): void => {
    navigate(`${location.pathname}${location.search}`, {
      preventScrollReset: true,
      state: { checkoutInFlowPush: Date.now() },
    });
  }, [location.pathname, location.search, navigate]);

  /**
   * Same-URL in-flow back (OS + in-app). Returns true when handled without navigate.
   * Cross-route exits (collect / missing-session / donation payment → shop) return false.
   */
  const popCheckoutStepBack = (): boolean => {
    if (activeStep === 'qr') {
      if (sessionIdFromUrl !== null) {
        persistCheckoutQrDemoteToPayment(sessionIdFromUrl);
      }
      void handleExitPaymentFlow();
      return true;
    }
    if (activeStep === 'payment') {
      if (paymentRailUiStep === 'gateway-methods') {
        setPaymentRailUiStep('primary');
        setGatewayMethodOptions([]);
        setPaymentError(null);
        return true;
      }
      if (isDonationSession) {
        return false;
      }
      // Persist before POP remount — restoreLeft briefly unmounts checkout (MOB-05).
      setCheckoutStepPersisted('collect');
      return true;
    }
    return false;
  };

  /**
   * Logical parent for system-back:
   * - same-URL pops (qr, payment->collect, gateway->primary) publish null
   * - cross-route exits (collect, missing-session, donation payment) publish shop path
   */
  const resolveCheckoutStepBackTo = (): string | null => {
    if (activeStep === 'payment' && !isDonationSession) {
      return null;
    }
    if (activeStep === 'qr') {
      return null;
    }
    return buildShopRecoveryPath();
  };

  return {
    t,
    activeStep,
    tenantCode,
    authSession,
    navigate,
    locationPathAndSearch,
    checkoutHelpOpen,
    setCheckoutHelpOpen,
    sessionIdFromUrl,
    activeQrPayment,
    checkoutSession,
    handleExitPaymentFlow,
    continueAfterPay,
    buildShopRecoveryPath,
    popCheckoutStepBack,
    resolveCheckoutStepBackTo,
    sessionError,
    sessionLoading,
    subtitleAmountMinor,
    subtitleCurrency,
    showCheckoutCurrencyPicker,
    checkoutCurrencyOptions,
    selectedCheckoutCurrency,
    currencyLocked,
    currencySwitchPending,
    currencySwitchError,
    handleCheckoutCurrencySelect,
    salesPointId,
    pickupPoints,
    pickupPointsQuery,
    collectPointsLoadError: collectPickupResolved.pointsLoadError,
    effectiveCollectSelection,
    scheduledPickupEnabled,
    tenantOpsMode,
    effectivePickupSlot,
    setPickupSlot,
    setCollectSelection,
    lockedCollectTiming,
    allowedPickupHandoffModes,
    effectiveHandoffMode,
    setSelectedHandoffMode,
    selfCollectOnlyGate,
    selfCollectConfirmAtKiosk,
    selfCollectConfirmed,
    setSelfCollectConfirmed,
    kioskInfo,
    collectContinueBlocked,
    updateCollect,
    handleCollectContinue,
    handleShippingContinue,
    shippingContinuePending,
    paymentError,
    paymentRailUiStep,
    setPaymentRailUiStep,
    gatewayMethodOptions,
    setGatewayMethodOptions,
    gatewayMethodsResponse,
    gatewayMethodsLoading,
    gatewayStartingMethodId,
    payActionsDisabled,
    handleGatewayMethodSelect,
    gateway,
    isDonationSession,
    selectedCheckoutMode,
    sessionCollect,
    pricingPreview,
    promotionsEnabled,
    loyaltyEnabled,
    promotionsProgramEnabled,
    promoPricingEnabled,
    showTypedPromoBlock: sellerAssistedCheckout ? false : showTypedPromoBlock,
    showPromoPreview: sellerAssistedCheckout ? false : showPromoPreview,
    showLoyaltyPreview: sellerAssistedCheckout ? false : showLoyaltyPreview,
    incentiveProgramsEnabled,
    promoEventNameById,
    promoExclusiveConflict,
    sellerAssistedCheckout,
    sellerAssistedDisplayLines,
    sellerAssistedStaleHandoff,
    sellerAssistedMerchantName,
    sellerAssistedSalesPointName,
    awaitingPaymentOnServer,
    pendingQr,
    resumedQrFromSession,
    handleResumeAwaitingPayment,
    email,
    setEmail,
    userEditedEmailRef,
    emailError,
    showSlaNotice,
    setSlaNoticeDismissed,
    mixedCartRefundTermLines,
    handleLowStockReservationExpired,
    holdExpiryReverify,
    setCheckoutStep: setCheckoutStepPersisted,
    visiblePaymentMethods,
    cashSignInRequired,
    mobileQrAllowed,
    qrRailCandidate,
    mobileGatewayAllowed,
    gatewayMethodsProbePending,
    gatewayMethodsPayable,
    gatewayRailCandidate,
    skipPaymentMethodPicker,
    paymentPickerOptions,
    selectedPaymentMethod,
    setSelectedPaymentMethodOverride,
    resolvePaymentConfirmLabel,
    handlePaymentConfirm,
    paymentConfirmPending,
    setPaymentError,
    startQr,
    startGateway,
    startCash,
    cashCompleteResult,
    cashCollectShellResult,
    cashPaymentConfirmed,
    allowCashContinueWithoutPaid,
    cashCollectOrderNumber,
    tenantDisplayName,
  };
}

export type UsePhoneFirstCheckoutScreenResult = ReturnType<typeof usePhoneFirstCheckoutScreen>;

/** Map checkout session DTO lines into ShopCartLine shape for {@link reVerifyAfterExpiry}. */
function mapCheckoutSessionLinesToShopCartLines(
  lines: readonly CheckoutSessionLineDTO[],
): ShopCartLine[] {
  return lines.map((line) => {
    const variantId = line.variantId ?? null;
    return {
      key: `${line.productId}:${variantId ?? 'base'}`,
      product: {
        id: line.productId,
        name: '',
        price: line.unitPrice,
      },
      variant:
        variantId === null
          ? null
          : {
              id: variantId,
              name: null,
              price: line.unitPrice,
              sku: null,
              barcode: null,
            },
      quantity: line.quantity,
      unitPrice: line.unitPrice,
    };
  });
}

